import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Common/Header';
// 需求 R101：報價單「編輯」頁補上與「建立」頁一致的縮圖 compact 截圖鈕（需求 R88 漏做本頁）。
import ScreenshotCapture from '../components/Products/ScreenshotCapture';
// 需求 R152（PM 2026-09-01）：產品縮圖 hover 下載圖片小圖示。
// 下載行為與產品詳情頁共用 utils/productImageDownloader.js，見該元件檔頭。
import ProductImageDownloadButton from '../components/Products/ProductImageDownloadButton';
import { usePermission } from '../utils/usePermission';
import { sortQuoteItems } from '../utils/productSort';
// 需求 R121 R2：保存期限取值（快照 → populate 產品 → 實時產品）與匯出共用同一支。
import { getItemShelfLife } from '../utils/quoteShelfLife';
// 需求 R136（PM 2026-08-24 拍板 Q1=B／Q5）：重複品項「照樣加入、只在搜尋結果列事前標示」。
// 本頁 items 由 GET /quotes/:id populate 而來，item.product 是物件；判定務必走 utils 的正規化。
import { DUPLICATE_ITEM_HINT, buildQuotedProductIdSet, isProductInQuote } from '../utils/quoteDuplicateItems';
import {
  getGlobalRate,
  getRateDecimals,
  isRateInputAllowed,
  validateCustomRate,
} from '../utils/customExchangeRate';
// 需求 R106 R6：選品區系列／類別的多選下拉改用與型錄管理相同的呈現（樣式單一來源）。
import '../styles/multiSelectDropdown.css';
// 需求 R123：Section 2 產品選取第二列新增「建立時間區間」欄位（沿用型錄新品目錄的 class 與樣式）。
import '../styles/dateRangeFilter.css';
import { validateProductDateRange } from '../utils/dateRange';
// 需求 R133 Q3=A：修復前存檔的舊單，自定成本殘留在 baseCost 但旗標已遺失 → 舊單保護的判定。
import { isQuoteSavedBeforeCustomCostFix, isLegacyCustomCostItem } from '../utils/legacyCustomCostGuard';
// 需求 R132（PM 2026-08-27）：單價算式收斂成單一共用模組（AC8），與 QuoteCreatePage.js 同一份。
// 本頁原本的 getBaseCostFromSource 是裸的 `product.factoryCost?.unitPrice ?? null`，
// 連建立頁早就有的「箱價 ÷ 箱入數」補算都沒有 → 同一筆產品在兩頁會算出不同單價（AC7）。
// 一併修掉 PM 回報的「1 階 61.0 應為 60.9／4 階 66.2 應為 66.1」：
// ‧ 根因 A 標準成本被無條件進位兩次 → 成本基準改讀未進位原值。
// ‧ 根因 B roundPrice 缺浮點雜訊防線 → 改走 quoteMoney.js 的 ceilMoney。
// 需求 R70 在產品管理端不變，詳見 utils/quoteUnitPrice.js 檔頭。
import {
  normalizeCostSource,
  resolveEntryBaseCost,
  getBaseCostFromSource,
  // 需求 R170（PM 2026-09-15 Q1=A／Q3=A）：「添加到報價單」與抽屜切換來源的 MOQ 都改由此 helper 取
  // 「使用此成本報價」勾選來源的成本項，與建立頁同一份實作（AC2／AC8）。
  getCostEntryFromSource,
  getEffectiveBaseCost,
  formatBaseCost,
  formatPrice,
  calcUnitPrice,
  // 需求 R145：冷凍系列判定（Q6=A 快照優先的三層 fallback）與建立頁共用同一支 util。
  resolveItemSeries,
  // 需求 R149：自訂箱成本 →（÷ 箱入數）→ 包成本。換算式只存在於 quoteUnitPrice.js，
  // 本頁與建立頁 import 同一份實作，不得各自複製一份（AC8）。
  packCostFromCustomBoxCost,
  canUseCustomBoxCost,
  CUSTOM_BOX_COST_NO_PCS_HINT,
} from '../utils/quoteUnitPrice';
// 需求 R151（PM 2026-08-31 Q4=A）：TIER_MAP／getDefaultPriceTier 原本是本頁與建立頁各一份的
// page-local 函式；產品詳細頁「歷史報價」的「!」也要用同一套規則算「價格階是否非預設」，
// 再複製第三份就會各自演化 → 抽到 utils/quotePriceTier.js，三處 import 同一份（AC6）。
import { getDefaultPriceTier } from '../utils/quotePriceTier';
// 需求 R148（PM 2026-08-31 第二則留言拍板 Q1=B 最後儲存時間／Q2=A／Q5=C／Q6=A／Q7=A／Q8=A／Q9=A）：
// 品項單價欄的「$」＝該列產品在「本報價單最後儲存時間」之後有敏感價格變更紀錄。
// 判定只讀既有的 GET /api/products/:id 回應（_productData.auditLog），零新增 API（AC17）。
import { hasSensitivePriceChange, SENSITIVE_PRICE_ALERT_TITLE } from '../utils/sensitivePriceAlert';
import './QuoteCreatePage.css';
import '../styles/sensitivePriceAlert.css';

const API = process.env.REACT_APP_API_URL || '/api';

// 需求 R103（PM 2026-08-03 留言「還有問題待釐清先不實作」）：
// 完整規格、新對照表與 Q1 的 A/C 差異分析寫在 QuoteCreatePage.js 同一個常數上方，
// 此處只記與「編輯頁」有關的差異，避免兩份獨立程式碼各記一半而漏改。
// 新對照表（尚未套用）：A 級 工廠2/經銷1、B 級 工廠3/經銷2、C 級 工廠4/經銷3、D 級 工廠4/經銷4。
//
// PM 拍板：Q2=A（無分級維持 1 階）、Q3=B（既有單開啟編輯頁即用新規則重算）、
// Q4=A（自訂成本仍看 costSource）、Q5=A（本頁一併補紅點）、Q6=B（不加 tooltip）、Q7=A（上線即生效）。
// Q1（切換成本來源要不要連動階數）仍待 PM 釐清，故本輪不動任何一行程式。
// Q9（2026-08-03 PM 追問「切成經銷1 後無論如何紅點都會存在」而長出的新題）：
// 「預設」要用活基準（隨客戶分級 × 本列成本來源即時重算，現況作法）還是
// 加入商品當下的凍結快照？完整對照寫在 QuoteCreatePage.js 的 TIER_MAP 上方。
// 對本頁的影響：Q5=A 要補的紅點基準值，得依 Q9 拍板結果決定「載入既有單時
// defaultPriceTier 要寫入重算後的新預設（Q9-A），還是寫入 DB 裡存的舊 priceTier（Q9-B）」，
// 這兩者在 Q3=B（開啟即重算）之下會直接決定「歷史單一打開是不是整排紅點」。
//
// 本頁實作時的三個已知缺口（Grep 實測，與建立頁不同，別直接複製貼上）：
// 1. 本檔沒有 normalizeCostSource（建立頁 QuoteCreatePage.js:223 才有），handleAddProducts
// 直接用 `p.costSource || 'factory'`。legacy 值 'distributor' 不會被正規化成
// 'distributor_0'，雙維查表時會落在「非工廠也非 distributor_N」的破口 → 需先補正規化。
// 2. 載入既有報價單的 setItems 映射完全沒有 defaultPriceTier（本頁沒有紅點機制），
// Q5=A 要補齊紅點就必須在這個映射裡逐列算出基準值。
// 3. Q3=B「開啟即重算」的落點也是這個映射：需依每列 costSource ＋ 當下客戶分級重算
// priceTier 並重算 unitPrice。注意後端 Quote schema（src/server/models/Quote.js:15）
// 只存 priceTier，沒有「使用者手動改過階數」的旗標，因此重算會連使用者上次存檔時
// 手動選的階數一起覆寫；若 PM 不接受，Q1 需選 C 並同步替 quoteItemSchema 新增旗標欄位。
//
// PM 2026-08-03 第三次追問（「現況」查證，明示只查不改）：「客戶自動帶 2 階 → 使用者
// 手動改 1 階 → 抽屜把成本來源由工廠改成經銷1，此時單價如何換算？」本頁與建立頁行為一致：
// ‧ handleCostSourceChange(:887-908) 沒有任何一行寫 item.priceTier → 階數維持手動的 1 階；
// 只換 baseCost(:895)、isCostSourceChanged(:896)、MOQ(:900)，再重算單價(:902-904)。
// ‧ 重算走 calcUnitPrice(:165-212)，階數取 item.priceTier || 1(:193)＝1 階
// → calcTierPrice(:79-88) 1 階＝ baseCost ÷1 + 16×材積÷箱入數，之後 ÷匯率 → 全單加成
// → 單品加價 → 貼標費 → 末端進位一次。
// ‧ 本頁差異：紅點條件(:1886)不含階數（缺口 2），故同一情境本頁 title 只會有
// 「成本來源已變更」一行，不會出現「價格階已調整為非預設」——Q5=A 補齊後才會一致。
// 結論：現況「切換成本來源不連動階數」。Q1 的 A / C 兩案都是新行為；若要維持現狀，
// 那是第三個選項（Q1=B 不連動），此選項下本頁單價與紅點行為零變更。
// 以上為 2026-08-03 的現況存查。PM 先選 B，再於 需求 R104（2026-08-04）改選 A＝一律連動，
// 實際行為以本檔下方 需求 R104 段落與 handleCostSourceChange 為準。
//
// ── Q3=B（本頁「開啟即用新規則重算」）落地前的五項查證（2026-08-03 實測，完整敘述在
// QuoteCreatePage.js 同一段註解的 F1~F5）──
// F1 分級拿得到：GET /quotes/:id 有 populate level（quoteController.js:100），本頁載入
// 當下即可算新階。但 setSelectedCustomer 只在 q.customer 是 populated 物件時才設
// （本檔 :404-406）；沒綁客戶的單（customer=null、只有 customerName 文字）分級不可得
// → 依 Q2=A 整單落 1 階。且重算落點若放在第一段 setItems 映射（:417）就要注意
// selectedCustomer 是同一個 .then 內先設好的，順序上安全，但「沒綁客戶」仍是破口。
// F2 歷史單 costSource 一律讀成 'factory'：schema 預設值即 'factory'
// （src/server/models/Quote.js:16），早於此欄位的舊單品項沒存值 → 新對照表下必定升一階。
// F3 只重算畫面不寫回：重算結果在本頁 state，未按儲存就離開時 DB 不變 →
// 詳情頁（QuoteDetailPage.js:448）與 PDF / Excel 匯出仍是舊價，兩邊不一致 → Q10-b。
// F4 已審核單也會被重算：本檔 isLocked = quoteStatus === 'approved'（唯讀進入），
// Q3=B 照字面會把已核准單重算成新價又存不回去 → 建議排除 isLocked 單 → Q10-c。
// ※ 2026-08-18 更新（需求 R119 Q4=A）：isLocked 已擴充為
// `approved || (pending && 非主管)`，故「待審核 + 業務」開啟時同樣不重算——
// 與 F4 的理由一致（唯讀進不去存檔，重算只會讓畫面價 ≠ 送審價）。
// F5 本檔 getBaseCostFromSource（:131-139）除了缺 normalizeCostSource，也缺建立頁
// resolveEntryUnitPrice 的「箱價 ÷ 箱入數」補算（QuoteCreatePage.js:210-221）：
// legacy 'distributor' 或「只有箱價沒單價」的成本項，在本頁切成本來源會把 baseCost
// 打成 null。此為既有缺口（非本票造成），AC4 補正規化時要連補算一起補齊。
//
// ── 2026-08-03 實作範圍（票由「待排期」移至「待處理」＝授權實作）──
// 本頁已實作：雙維對照表＋getDefaultPriceTier()、normalizeCostSource（AC4，與建立頁同義）、
// 加入商品逐列判定（AC5）、Q3=B 載入即以新規則重算 priceTier 並重算單價（AC9）、
// Q5=A 紅點補上「價格階非預設」一行、換客戶時逐列重刷 defaultPriceTier。
// Q3=B 的兩個保護（見 F1 / F4，實作時的明確假設，請 PM 確認 → 任務 Q10）：
// ‧ 沒綁客戶（拿不到分級）的單不重算，保留存檔的 priceTier —— 否則整單會被打成 1 階。
// ‧ 已審核（isLocked）的單不重算 —— 唯讀進不去存檔，重算只會讓畫面價 ≠ 已核准價。
// 這兩種情況下 defaultPriceTier 直接對齊存檔值，避免整排誤報紅點。
// Q1=B（PM 第三次留言「Q1 已回覆，不連動」）—— 已被 需求 R104 Q1=A 推翻，見本檔下段。
//
// ── PM 2026-08-03 第四次留言，Q8 / Q9 拍板（本輪落地）──
// Q8=A（手動調過的階數重開編輯頁要保住）：後端 quoteItemSchema 新增 isManualPriceTier，
// 兩頁存檔 payload 一併送；本頁載入既有單時，Q3=B 的重算只作用在「非手動」的列，
// 旗標為真的列保住存檔階數（ruleTier 只拿去當紅點基準 defaultPriceTier）。
// 已知邊界：上線前的舊單沒有這個欄位（讀回為 default false），「上線前手動調過的階數」
// 無從辨識，仍會被 Q3=B 重算一次。已在需求討論中標明。
// 旗標的作用範圍：載入既有單時不被 Q3=B 覆寫（需求 R104 之後，切成本來源則會覆寫它）。
// Q9（紅點以「手動調整（非預設）」為語意）：仍寫成 priceTier ≠ defaultPriceTier，
// 由 applyDefaultTier() 撐住不變式「旗標為真 ⇔ 本列階數非預設」使兩者等價。
// 不直接綁旗標的理由寫在 QuoteCreatePage.js 的 TIER_MAP 上方（AC9 兩個保護會自相矛盾）。
//
// ── 需求 R104：PM 2026-08-04 拍板 Q1=A / Q2=A（推翻 需求 R103 Q1=B）──
// Q1=A「一律覆寫成新來源的預設階。但一但『重新選擇』意指非預設，則啟動驚嘆號」
// → handleCostSourceChange 改呼叫 applyDefaultTier(item, level, undefined, true)：
// 階數重算為「客戶分級 × 新來源」，手動列一併覆寫、isManualPriceTier 歸零；
// 之後使用者自己再挑非預設階時紅點才重新亮（updateItem 的 nextTier 路徑，未變）。
// Q2=A「MOQ 清空」→ MOQ 一律換成新來源的值，新來源沒填就清空，不再沿用舊來源。
// Q3 / Q4 / Q5＝OK：不批次改寫既有單；未綁客戶（拿不到分級）與已審核單維持現行保護
// （本頁 handleCostSourceChange 兩者都擋）；isManualPrice 的列單價仍不重算。
// 完整敘述與 fixture 反轉說明見 QuoteCreatePage.js 同名段落。
//
// ── 需求 R133：PM 2026-08-21 拍板 Q1=B / Q2=A / Q3=A / Q4=B / Q5=A ──
// 本票的根因在後端：quoteItemSchema 沒宣告 customCostEnabled / customCostValue，
// mongoose strict mode 靜默丟棄 → 主管重開單時勾選與數值都不見、紅色驚嘆號也不亮。
// 修復是 src/server/models/Quote.js 補上兩個欄位；本頁的送出（:1432-1433 payload）與
// 讀回（:598-600）邏輯本來就正確，不需改動。以下三項是拍板結果在本頁的落地：
//
// Q1=B「審核／已鎖定狀態下主管可直接修改」→ 自定成本的勾選框與輸入框改綁具名條件
// canEditCustomCost，並在 handleCustomCostToggle / handleCustomCostChange 補上
// handler 層防線（原本只有 UI 層 disabled，比照 handleCostSourceChange 的雙重把關）。
// 語意：待審核 (pending) 的單，主管開啟即可直接改自定成本（isLocked 對 manager 為
// false，本來就允許，本次把它寫成明確條件而非隱含結果）。
// 已審核 (approved) 的單**不**開放：後端 quoteController.update 對 approved 一律
// 403（quoteController.js「已審核：任何人皆不可修改」），開放輸入只會產生存不回去的
// 假編輯，也會與 需求 R119 Q4=A 的整頁唯讀自相矛盾。此邊界已在需求討論中標明；
// 若 PM 要連已審核單也能改自定成本，必須另案一併改後端閘門。
//
// Q2=A「舊單一律顯示為未使用自定成本」→ 不做任何資料回填、不寫遷移腳本；
// schema default（false / 0）就是舊單的顯示結果（:598-600 的讀回不變）。
//
// Q3=A「舊單要加保護」→ 修復前的舊單雖然旗標遺失，自定成本的數字其實還留在
// item.baseCost（它是計算基準的快照）。因此本頁在載入既有單、取得產品資料後逐列判定
// isLegacyCustomCostItem()（utils/legacyCustomCostGuard.js），命中就掛上
// _legacyCustomCostGuard，並且：
// ‧ calcUnitPrice 開頭比照 isManualPrice 早退 → 任何自動重算都不覆寫存檔單價；
// ‧ handleCostSourceChange 直接擋下（那是唯一會把 baseCost 換成來源成本的路徑，
// 也就是「無聲改價」的真正入口）並跳提示；
// ‧ 抽屜顯示黃字說明＋「改用來源成本重算」鈕，使用者要嘛重新勾自定成本輸入數值
// （updateCustomCost 會解除保護），要嘛明確按鈕解除，不會被凍死；
// ‧ 儲存時若還有未處理的列，跳 confirm 告知「儲存後系統不再標記這些列」。
// 校正任務描述的一處說法：改階數／改單品加成／改貼標走的是 updateItem，該函式**不**
// 重算 baseCost（只重算 unitPrice，成本基準仍是存檔的自定成本值），所以無聲改價的
// 入口只有 handleCostSourceChange 與 updateCustomCost。保護仍一併涵蓋自動重算路徑，
// 因為那些路徑會把「存檔單價」換成「用同一 baseCost 重算的新單價」，對舊單同樣是
// 人沒動過卻變了金額。
//
// Q4=B「驚嘆號 tooltip 維持現狀」→ 不加數值、不動兩頁 title 文案。
// Q5=A「不納入匯出／auditLog」→ 匯出與稽核路徑一行不動。
// 需求 R151：TIER_MAP 與 getDefaultPriceTier 的本體已移到 utils/quotePriceTier.js
// （本頁 import 進來，見檔頭 import 區）。值與行為一字未改，只是把「兩頁各一份」
// 收斂成「三處共用一份」——產品詳細頁「歷史報價」的「!」是第三個使用者。

// 需求 R103（AC4）：本頁原本沒有 normalizeCostSource，legacy 無索引 'distributor' 會落在
// 「非 factory 也非 distributor_N」的破口（雙維查表查不到、getBaseCostFromSource 回 null）。
// 需求 R132：改由 utils/quoteUnitPrice.js 提供，與建立頁保證同一份實作。


// 需求 R103（PM 2026-08-03 Q8=A / Q9）：把「當下規則預設階」套回單一列，維持不變式
// 「isManualPriceTier 為真 ⇔ 本列階數非預設」。與 QuoteCreatePage.js 同一份邏輯
// （兩頁各自獨立一份，改動須同步；fixture 會斷言兩頁行為一致）。
// ‧ 非手動列：priceTier 直接跟著規則走 → 不留下與規則脫節、卻因為沒旗標而不警示的列。
// ‧ 手動列（Q8=A）：保住使用者選的階數，只更新比較基準 defaultPriceTier；
// 若手動值剛好等於新預設，旗標歸零（依 Q9 語意紅點應熄滅）。
// nextTier == null（換客戶）走「非手動列跟規則」分支；傳入 nextTier（updateItem）
// 視為使用者主動選階，旗標置 true（除非選回預設值）。
// 需求 R104（PM 2026-08-04 Q1=A，推翻 需求 R103 Q1=B）：第四個參數 forceDefault ——
// 切成本來源時「一律覆寫成新來源的預設階」，手動列一併覆寫、旗標歸零；覆寫後使用者若再
// 手動挑非預設階，紅點才重新亮（走 nextTier 分支）。與建立頁同一份邏輯，fixture 會比對兩頁。
function applyDefaultTier(item, level, nextTier, forceDefault) {
  const newDefault = getDefaultPriceTier(level, item.costSource);
  const isUserPick = nextTier != null;
  const tier = isUserPick ? nextTier : item.priceTier;
  const isManual = !forceDefault
    && (isUserPick ? true : !!item.isManualPriceTier)
    && Number(tier) !== Number(newDefault);
  return {
    ...item,
    defaultPriceTier: newDefault,
    priceTier: isManual ? tier : newDefault,
    isManualPriceTier: isManual,
  };
}
// 需求 R90：選擇商品搜尋每頁筆數（與新增頁 QuoteCreatePage.js 的 PRODUCT_PAGE_SIZE 一致，
// 配合後端 productController getAll 的 page/limit 分頁；原本編輯頁硬寫 { limit: 50, page: 1 }
// 永遠只抓第一頁，導致同一組篩選條件在編輯頁只看得到 50 筆）。
const PRODUCT_PAGE_SIZE = 50;

// 需求 R132（PM 2026-08-27）：calcTierPrice / applySpecialFormula / roundPrice / formatPrice /
// getEffectiveBaseCost / getBaseCostFromSource / calcUnitPrice 全數移到
// src/client/src/utils/quoteUnitPrice.js，本頁與建立頁 import 同一份（AC8）。
// ‧ 本頁舊版 getBaseCostFromSource 只讀 `?.unitPrice ?? null`（已進位值、且缺箱價補算），
// 現在與建立頁同走 resolveEntryBaseCost「箱價÷箱入數（未進位原值）優先」（AC3／AC7）。
// ‧ 舊版 roundPrice 的裸 Math.ceil 已改走 quoteMoney.js ceilMoney（含浮點雜訊防線，AC5）。
// 任何要調整單價算式的人請直接改 utils/quoteUnitPrice.js，不要在本頁再開一份。

// 需求 R82（PM 2026-07-22 拍板 D1~D4）：報價單單價運算重定義。
// 本單重新定義「特殊公式」欄位的行為，與過往描述方向不同，以本單為準。
// - 情境一 特殊公式「有值」：「價格階數」直接失效不套用；單價 = 標準成本 →（套用特殊公式）
// → 全單加成 → 單品加價 → 貼標費用。
// - 情境二 特殊公式「為空」：維持階數運算；單價 = 標準成本 → 階數 → 全單加成 → 單品加價 → 貼標費用。
// PM 拍板決策：
// - D1 材積成本：特殊公式生效、階數停用時「純以標準成本進特殊公式」，不加材積成本(16×材積/箱入數)。
// - D2 加成順序：確定改為「全單加成 → 單品加價」（先套百分比乘法，再加單品加價）。
// - D3 幣別匯率（需求 R83 PM 2026-07-24 缺陷修正——換算「時機」）：非 TWD 幣別時，只在
// 「TWD 成本基礎」全部算完後換算一次。 修正 需求 R82「先 ÷匯率 再算階數」的缺陷：
// 材積成本 16×材積/箱入數 屬 TWD 計價，若換算置於階數之前，材積成本會被直接加到目標幣別
// 單價上而未一起換算，導致換算後單價異常偏高（PM 案例：USD 30.8、成本 80、材積 1.19，
// 預期 (80+1.19)/30.8=2.636→2.64，錯誤算成 80/30.8+1.19=3.79）。
// ‧ 情境二（階數/材積）：在 TWD 上算完標準成本+階數+材積 → 再 ÷匯率 一次。
// ‧ 情境一（特殊公式）：維持先 ÷匯率 再套公式（公式為非匯率純係數、順序不影響結果）。
// (a) 匯率方向正確且「動態跟隨輸入」：rate 取自後端 /exchange-rates 的 hkd_quote/usd_quote/
// eur_quote（getCurrentRate），並非寫死常數；匯率數值變動時 useEffect（依賴 exchangeRates）
// 會重算所有品項單價，故報價會跟著當次設定的匯率調整（PM 2026-07-24 確認：匯率非固定值）。
// (b) 特殊公式「不會」造成重複換算：PM 2026-07-24 明確「特殊公式是非匯率算法、僅是特殊公式」，
// 其常數（如 *1.03）屬單純加成係數、不含匯率換算，故與單次 ÷匯率 不衝突、無重複扣匯率問題。
// (c) 單品加價 itemMarkup、貼標費用 labelingFee 本就以目標幣別儲存，換算已於前面完成，直接相加即可。
// - D4 進位時機：只在最後換算結果進位一次。
// isManualPrice（手動單價）維持最優先、不受本次調整影響。
// ※ 與 QuoteCreatePage.js 的 calcUnitPrice 行為需完全一致，兩處同步修改。

// 需求 R126：Section 3「全單加成 (%)」輸入框允許的字面值（與 QuoteCreatePage.js 同名常數必須一致）。
// ‧ 不含負號 ← PM 2026-08-21 拍板 Q3「不支援負數」。
// ‧ `\d*\.?\d{0,3}` 允許中間態「1.」「0.」「.」，最多小數點後 3 位（Q1 PM 未填，暫用建議值）。
const GLOBAL_MARKUP_INPUT_RE = /^\d*\.?\d{0,3}$/;

const QuoteEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState('');

  // Section 1
  const [quoteNumber, setQuoteNumber] = useState('');
  const [quoteDate, setQuoteDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [companyHeader, setCompanyHeader] = useState('皇聚');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [customerInput, setCustomerInput] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currency, setCurrency] = useState('TWD');
  const [exchangeRates, setExchangeRates] = useState({});
  // 需求 R97：單筆報價單的自訂匯率（與 QuoteCreatePage 同一組狀態與規則，兩頁行為必須一致 AC9）。
  const [customRate, setCustomRate] = useState(null);
  const [rateUnlocked, setRateUnlocked] = useState(false);
  const [rateInput, setRateInput] = useState('');
  const [specialFormula, setSpecialFormula] = useState('');
  const customerDebounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Section 2
  const [productKeyword, setProductKeyword] = useState('');
  // 需求 R85（PM 2026-07-24 留言「A」）：Section 2「產品選取」整段可收合，
  // 預設收合（AC7=A），與建立頁行為一致。切換僅隱藏 .qc-section-body（display:none），
  // 元件不卸載，故搜尋條件 / 搜尋結果 / 已勾選商品 / 載入更多分頁狀態於收合再展開後完整保留。
  const [productPickerCollapsed, setProductPickerCollapsed] = useState(true);
  // 需求 R106 Q2=A（PM 2026-08-04「四頁統一改多選」）：系列／類別由單選字串改為
  // 多選陣列，與建立頁同步（兩頁是兩份獨立 code，改一邊必須同時改另一邊）。
  const [productSeries, setProductSeries] = useState([]);
  const [productCategory, setProductCategory] = useState([]);
  // 需求 R29 / 需求 R59 D5(A)：編輯頁保留「產品標籤」多選篩選；葷素備註篩選面板已移除。
  const [productTags, setProductTags] = useState([]);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const tagsDropdownRef = useRef(null);
  // 需求 R106 R6：系列／類別多選下拉各自的開／關 state + click-outside 用的 ref。
  // 呈現改用共用的 .multi-select-*（styles/multiSelectDropdown.css，四頁同一份來源），
  // 與型錄管理／產品管理／建立頁對齊；R3 時原本沿用「標籤」用的 .tag-dropdown 已淘汰。
  const [seriesDropdownOpen, setSeriesDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const seriesDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [searchLoading, setSearchLoading] = useState(false);
  // 需求 R90（移植 需求 R73 新增頁「載入更多」）：後端 getAll 已支援 page/limit 並回傳 total，
  // 前端逐頁 append 累加，跨頁不清空既有結果，已勾選的 selectedProductIds（Set of _id）保留。
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);
  // 記錄「上次實際送出搜尋」的篩選字串（不含 page/limit），供「載入更多」沿用同一組條件，
  // 避免使用者改了篩選欄位但尚未按「搜尋」時，載入更多帶到不一致的條件。
  const lastSearchParamsRef = useRef('');
  // 需求 R123（PM 2026-08-19 拍板 Q1=B 空白 / Q2=B 只填一格也可搜尋 / Q3=B 清除同清空
  // / Q4=A 沿用「即時 state + 按鈕觸發」）：Section 2 第二列新增「建立時間區間」欄位。
  // 與建立頁 (QuoteCreatePage.js) 是兩份獨立 code，行為與 state 名稱完全對齊（需求 R90 教訓）。
  const [productCreatedFrom, setProductCreatedFrom] = useState('');
  const [productCreatedTo, setProductCreatedTo] = useState('');
  const [productDateError, setProductDateError] = useState('');

  // Section 3
  const [items, setItems] = useState([]);
  const [globalMarkup, setGlobalMarkup] = useState(0);
  // 需求 R126：全單加成輸入框的「原始字串 buffer」。null＝沒在編輯（畫面顯示數字 state），
  // 非 null＝使用者正在輸入，畫面顯示此字面值以保留「1.」「0.」等小數中間態。
  // （與 QuoteCreatePage.js 同步；沿用 需求 R84 單品加價 _editingItemMarkup 的既有範式。）
  const [editingGlobalMarkup, setEditingGlobalMarkup] = useState(null);
  const [labelingFees, setLabelingFees] = useState({});
  const [infoPanel, setInfoPanel] = useState(null);

  // Section 4
  const [transactionNotes, setTransactionNotes] = useState('');
  // 需求 R143（PM 2026-08-29 Q1=A 非必填）：與交易條件並列的第二欄「備註」（schema: internalNotes）。
  const [internalNotes, setInternalNotes] = useState('');
  // 需求 R143 Q6=A：編輯頁的「建立者」顯示**該單原始建單人**（不是目前登入者）。
  // 來源＝GET /api/quotes/:id 已 populate('createdBy','name')（quoteController.js:89）。
  // 純顯示用，不進 payload——後端 update 也已明確剔除 req.body.createdBy（AC14）。
  const [createdByName, setCreatedByName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [salesUsers, setSalesUsers] = useState([]);

  const [saving, setSaving] = useState(false);
  // 需求 R162 Q1=A：「儲存備註」小鈕自己的忙碌旗標——與整張單的 saving 分開，
  // 否則在唯讀頁存備註時會連帶把不存在的儲存大鈕邏輯攪進來。
  const [savingNotes, setSavingNotes] = useState(false);
  const [error, setError] = useState('');
  const [loadingQuote, setLoadingQuote] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  // 需求 R125（PM 2026-08-21 拍板）：編輯頁補「審核退回」入口，
  // 退回是不可逆的狀態變更（pending → rejected），故先跳確認視窗再送出。
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [quoteStatus, setQuoteStatus] = useState('draft');
  // 需求 R148 Q1=B（PM 2026-08-31 第二則留言：「比較基準時間 (B) 最後儲存時間」）：
  // 「$」的基準時間＝這張單載入當下的 updatedAt（Quote schema 的 timestamps）。
  // ‧ 與報價單狀態無關（AC7）：草稿／審核中／退回／已核准都用同一個欄位，
  // 已核准單因為禁止再編輯，updatedAt 天然停在核准當下（＝「$」會持續保留，AC10 後半）。
  // ‧ 已知殘留：markExported（列印／匯出標記）會推進 updatedAt → 按過列印的單，
  // 基準時間會被推到列印當下（原 Q11-A 的破口）。PM 本輪明示「忽略 Q11」，
  // 故本票**不改** markExported 的既有行為，僅在評論向 PM 標明。
  const [quoteUpdatedAt, setQuoteUpdatedAt] = useState(null);
  const [lastEditTime, setLastEditTime] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef(null);
  const [toasts, setToasts] = useState([]);
  const [editingField, setEditingField] = useState(null); // { idx, field }
  const imageInputRef = useRef(null);
  const imageUploadCtxRef = useRef(null); // { productId } target for upload
  // 需求 R103（Q3=B）：載入時是否有依新規則改動過 priceTier → 決定要不要跑一次性單價重算。
  // 由載入流程寫入（沒綁客戶 / 已審核單為 false），下方 useEffect 消費後歸零。
  const tierRecalcPendingRef = useRef(false);
  // 需求 R133 Q3=A：本張單是否存於「schema 補欄位」修復之前 → 決定要不要逐列判定舊單保護。
  // 由載入流程依 q.updatedAt 寫入；只在載入既有單時消費一次。
  const savedBeforeCustomCostFixRef = useRef(false);

  const { can } = usePermission(user);
  // 需求 R119（PM 2026-08-18 拍板 Q2=A／Q3=A／Q4=A／Q6=A）：報價單狀態＋角色決定本頁能否編輯。
  // ‧ 草稿 (draft)：可編輯（沿用 quote.edit 權限，sales/manager 皆可）。
  // ‧ 退回 (rejected)：Q6=A「比照草稿」→ 可編輯。
  // ‧ 待審核 (pending)：Q2=A「只有主管能改，其他所有角色都不能改」→ 非 manager 一律整頁唯讀，
  // Q4=A「比照已審核，整頁唯讀（isLocked 條件擴充），只留列印與返回」。
  // 本條**反轉** 需求 R33（PM 2026-05-22 曾開放「pending + sales 可編輯、可送審」）；
  // PM 在本票 Q3 明確回覆「A. 確認反轉，以本票為準」，故舊決議自 2026-08-18 起失效。
  // 後端 PUT /api/quotes/:id 亦補了同一道閘門（quoteController.update），防 API 直呼。
  // ‧ 已審核 (approved)：任何角色皆唯讀（原規則不變）。
  // 唯讀時所有欄位/按鈕禁用；仍允許點擊產品「眼睛」開啟資訊抽屜檢視成本來源與設定，但抽屜內的選項也唯讀。
  // 註：user 由 useEffect 非同步塞入，未載入時 isManager=false → pending 單先鎖住（fail-safe 方向）。
  const isManager = user?.role === 'manager';
  const isLocked = quoteStatus === 'approved' || (quoteStatus === 'pending' && !isManager);
  const canInlineEdit = (can('quote.create') || can('quote.edit')) && !isLocked;
  // 需求 R162（PM 2026-09-06 拍板 Q1=A／Q3=A／Q5=A）：
  // 「備註」(internalNotes) 在**四種狀態下都能編輯**（Q5=A：草稿／待審核／已審核／審核退回全開），
  // 所以它必須脫離整頁共用的 isLocked，改綁自己的條件。
  // 這裡刻意**只**放行備註一欄：isLocked 的其他用途（客戶／匯率／全單加成／品項各欄／
  // 交易條件／聯絡人 disabled、Section 2 產品選取整段隱藏、底部寫入鈕不渲染）一律不動，
  // 否則就從「開放一個欄位」變成「把整張已審核單解鎖」（AC6）。
  // Q3=A：權限沿用 quote.edit ＝ 業務＋主管可改、採購不可（後端 PATCH /:id/internal-notes
  // 同樣掛 canEditQuote，前端隱藏只是體驗，後端 403 才是防線）。
  const canEditInternalNotes = can('quote.edit');
  // Q1=A：唯讀狀態下畫面上一顆寫入鈕都沒有（需求 R119 Q3/Q4=A 拍板「唯讀＝純檢視」），
  // 所以備註欄下方要單獨長出一顆「儲存備註」小鈕，只送備註、不動其他欄位。
  // 非唯讀（draft／rejected／pending+主管）時**不**顯示這顆鈕——那些狀態底部本來就有
  // 「儲存」大鈕會連備註一起存（AC2／AC3 現況不回歸），多一顆只送備註的鈕反而會讓使用者
  // 誤以為整張單都存好了。
  const showNotesOnlySave = isLocked && canEditInternalNotes;
  // 需求 R133（PM 2026-08-21 Q1=B「主管可直接修改」）：自定成本的勾選與數值
  // 由這個具名條件把關，不再只是隱含在 isLocked 裡。
  // ‧ 待審核 (pending) + 主管 → isLocked 為 false ＝ 可直接修改（Q1=B 要的情境）。
  // ‧ 待審核 + 非主管、已審核 (approved) → 不可修改：後端 update 對這兩種情況都會 403
  // （quoteController.update），開放輸入只會做出存不回去的假編輯。
  // 為何要另外命名而不是直接寫 !isLocked：這是 PM 對「自定成本」單獨拍板的權限點，日後若
  // 要放寬（例如連已審核單也讓主管改）只需改這一行＋後端閘門，不必再翻整頁的 isLocked。
  const canEditCustomCost = !isLocked;
  // 需求 R119（Q1=B）：草稿／退回不可列印，只有待審核與已審核可列印——條件與詳情頁
  // QuoteDetailPage.js「列印 / 匯出」按鈕完全一致，三個入口不再各說各話。
  const canPrint = quoteStatus === 'pending' || quoteStatus === 'approved';
  // 需求 R125（PM 2026-08-21 留言拍板 Q1=A）：
  // 「列表『操作』欄一律導向編輯頁（需求 R33）這點維持不變，但待審核的報價單進到編輯頁時
  // 要出現『審核退回』按鈕，主管權限同時保留『儲存並審核』。」
  // → 顯示條件與詳情頁 QuoteDetailPage.js:227/247-256 的退回鈕完全一致（status==='pending' && manager），
  // 兩個入口同一套規則，不再各說各話；後端 quoteController.updateStatus:216-236 亦只允許
  // pending→rejected 且 role==='manager'，非主管直呼 API 一樣 403（前端隱藏≠後端安全）。
  const canReject = quoteStatus === 'pending' && isManager;

  const pushToast = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  // Auth
  useEffect(() => {
    const t = localStorage.getItem('token');
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (!t || !u._id) { navigate('/login'); return; }
    setToken(t);
    setUser(u);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Fetch initial data + existing quote
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    // Exchange rates
    fetch(`${API}/exchange-rates`, { headers })
      .then(r => r.json())
      .then(d => {
        const map = {};
        if (Array.isArray(d)) {
          d.forEach(r => { map[r.key] = r.rate; });
        } else if (d && typeof d === 'object') {
          Object.assign(map, d);
        }
        setExchangeRates(map);
      })
      .catch(() => {});

    // Companies
    fetch(`${API}/companies`, { headers })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setCompanyOptions(d); })
      .catch(() => {});

    // Product options
    fetch(`${API}/products/options?type=series`, { headers })
      .then(r => r.json())
      .then(d => setSeriesOptions(Array.isArray(d) ? d.map(o => o.value) : []))
      .catch(() => {});
    fetch(`${API}/products/options?type=category`, { headers })
      .then(r => r.json())
      .then(d => setCategoryOptions(Array.isArray(d) ? d.map(o => o.value) : []))
      .catch(() => {});
    // 需求 R59：不再抓取 vegetarianType 選項；filter panel 與品項備註都改純文字。
    fetch(`${API}/products/options?type=tag`, { headers })
      .then(r => r.json())
      .then(d => setTagOptions(Array.isArray(d) ? d : []))
      .catch(() => {});

    // Labeling fees
    fetch(`${API}/labeling-fees`, { headers })
      .then(r => r.json())
      .then(d => {
        const map = {};
        if (Array.isArray(d)) {
          d.forEach(f => { map[f.key] = f.fee; });
        } else if (d && typeof d === 'object') {
          Object.assign(map, d);
        }
        setLabelingFees(map);
      })
      .catch(() => {});

    // Sales users
    fetch(`${API}/users`, { headers })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.users || []);
        setSalesUsers(list.filter(u => u.role === 'sales' || u.role === 'manager'));
      })
      .catch(() => {});

    // Load existing quote
    fetch(`${API}/quotes/${id}`, { headers })
      .then(r => {
        if (!r.ok) throw new Error('載入失敗');
        return r.json();
      })
      .then(q => {
        // 需求 R119（取代 需求 R33 的說明）：草稿 / 退回 → 可編輯；
        // 待審核 → 主管可編輯、其他角色進入後整頁唯讀；已審核 → 任何人進入皆唯讀。
        // 四種狀態都允許「進入」（列表頁的「檢視」也是導到本頁），唯讀與否在 render 階段由 isLocked 決定。
        // 取消 (cancelled) 或其他未預期狀態才阻擋進入。
        const allowedStatuses = ['draft', 'rejected', 'pending', 'approved'];
        if (!allowedStatuses.includes(q.status)) {
          setError('此報價單目前無法檢視');
          setLoadingQuote(false);
          return;
        }
        setQuoteStatus(q.status || 'draft');
        // 需求 R148 Q1=B：本報價單的最後儲存時間，供品項列「$」判定用（AC9）。
        setQuoteUpdatedAt(q.updatedAt || null);
        setQuoteNumber(q.quoteNumber || '');
        setQuoteDate(q.quoteDate ? q.quoteDate.slice(0, 10) : '');
        setExpiryDate(q.expiryDate ? q.expiryDate.slice(0, 10) : '');
        setCompanyHeader(q.companyHeader || '皇聚');
        setCustomerInput(q.customerName || q.customer?.name || '');
        if (q.customer && typeof q.customer === 'object') {
          setSelectedCustomer(q.customer);
        }
        setCurrency(q.currency || 'TWD');
        // 需求 R97（AC8 / Q5=A）：修補「編輯頁完全不讀 quote.exchangeRate、一律用今天的全域匯率重算」
        // 的既有缺口。只有標記為自訂匯率的報價單才鎖定存檔值還原；未標記者（含所有舊單）
        // 維持現行的跟隨全域匯率行為，影響面最小。
        if (q.isCustomExchangeRate && Number(q.exchangeRate) > 0) {
          setCustomRate(Number(q.exchangeRate));
        }
        setSpecialFormula(q.specialFormula || '');
        setGlobalMarkup(q.globalMarkup || 0);
        // 需求 R103（Q3=B / Q5=A）：既有單開啟時依「客戶分級 × 該列成本來源」以新規則重算
        // priceTier，並寫入紅點基準 defaultPriceTier（本頁原本完全沒有這個欄位）。
        // 分級只能取自這裡的 q.customer（getOne 有 populate level）；上面剛呼叫的
        // setSelectedCustomer 在本 map 的閉包裡還讀不到，故直接用 q.customer.level。
        // 兩個不重算的保護見本檔頂部註解（沒綁客戶 / 已審核唯讀單）。
        // 需求 R133 Q3=A：這張單存於修復之前嗎？（真正的逐列判定要等產品資料回來才有來源成本可比）
        savedBeforeCustomCostFixRef.current = isQuoteSavedBeforeCustomCostFix(q.updatedAt);
        const loadedLevel = (q.customer && typeof q.customer === 'object') ? q.customer.level : null;
        const skipTierRecalc = !loadedLevel || q.status === 'approved';
        // 不重算的單也不要跑下方的一次性單價重算，否則已核准單的畫面價會偏離存檔價。
        tierRecalcPendingRef.current = !skipTierRecalc;
        setItems((q.items || []).map(item => {
          const loadedCostSource = normalizeCostSource(item.costSource || 'factory');
          const ruleTier = getDefaultPriceTier(loadedLevel, loadedCostSource);
          const storedTier = Number(item.priceTier) || 1;
          // 需求 R103（Q8=A）：存檔時業務手動選的階數要保住，不被 Q3=B 的重算蓋掉。
          // 旗標為真 → priceTier 沿用 storedTier，ruleTier 只當紅點基準 defaultPriceTier
          // （storedTier ≠ ruleTier 時紅點會亮，正是 Q9 要的「手動調整才出現」）。
          // 舊單沒有這個欄位 → 讀回 undefined → 視為非手動，照 Q3=B 重算（已知邊界）。
          // 兩個保護（沒綁客戶 / 已審核）之下 defaultPriceTier 對齊存檔值（AC9），
          // 此時旗標維持存檔值不歸零 —— 使用者補選客戶後，換客戶的 useEffect 才有依據
          // 認出這是手動列而保住階數；紅點在對齊期間不亮，與 AC9「避免整排誤報」一致。
          const storedManualTier = !!item.isManualPriceTier;
          const keepStoredTier = skipTierRecalc || storedManualTier;
          return ({
            ...item,
            costSource: loadedCostSource,
            priceTier: keepStoredTier ? storedTier : ruleTier,
            defaultPriceTier: skipTierRecalc ? storedTier : ruleTier,
            isManualPriceTier: skipTierRecalc
              ? storedManualTier
              : (storedManualTier && storedTier !== ruleTier),
            productImage: item.productImage || '',
            salesNotes: item.salesNotes || '',
            // 需求 R121 R2：舊報價單的品項快照沒有 shelfLife，改由 getItemShelfLife 從
            // populate 回來的 item.product 補上；否則本頁匯出（buildExportQuoteObj）取不到值，
            // PM 回報的「保存期限沒顯示」就是從這裡開始斷掉的。
            shelfLife: getItemShelfLife(item),
            // 需求 R145（Q6=A）：系列快照。新單直接讀 item.series；本票上線前的舊單沒有這個
            // 欄位，resolveItemSeries 會退回 getById 的 .populate('items.product', '... series')
            // —— 這一步在**第一次 setItems 就完成**，因此下方非同步補抓 _productData 的空窗期
            // 不會把冷凍品誤算成 16（AC8）。
            series: resolveItemSeries(item),
            // 需求 R32：舊報價單無 customCost 欄位時，預設停用且值為 0。
            customCostEnabled: !!item.customCostEnabled,
            customCostValue: Number(item.customCostValue) || 0,
            // 需求 R149 Q7=A：舊單同樣沒有這兩個欄位（不回寫、不遷移），讀回一律 false / 0
            // ＝「未使用自訂箱成本」，單價與箱價與本票之前完全相同（AC19 零回歸）。
            customBoxCostEnabled: !!item.customBoxCostEnabled,
            customBoxCostValue: Number(item.customBoxCostValue) || 0,
            _productData: null,
            _origProductImage: item.productImage || '',
            _origEnglishName: item.englishName || '',
            // 需求 R118（Q8=A「清掉」）：salesNotes 不再回寫，比對用的 _origSalesNotes 快照一併移除。
            // 需求 R34（PM 2026-05-22 留言）：編輯既有報價單時，
            // 若無法立刻取得 Product.vegetarianType，先以報價單上的 vegetarianNote 當作起點，
            // 避免「沒改卻被回寫」。等下方 product fetch 完再用實時 Product.vegetarianType 覆蓋。
            _origVegetarianType: item.vegetarianNote || '',
          });
        }));
        // Fetch full product data for drawer
        // 需求 R136 Q1=B：同一產品現在可能出現在多列 → 先去重再打 API，
        // 否則重複列會對 /products/:id 發出重複請求（productMap 以 _id 為鍵，結果完全相同）。
        const productIds = [...new Set(
          (q.items || [])
            .map(item => typeof item.product === 'object' ? item.product?._id : item.product)
            .filter(Boolean)
            .map(String)
        )];
        if (productIds.length > 0) {
          Promise.all(productIds.map(pid =>
            fetch(`${API}/products/${pid}`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null)
          )).then(products => {
            const productMap = {};
            products.forEach(p => { if (p) productMap[p._id] = p; });
            // 需求 R133 Q3=A：本批判定命中幾列（判定寫在 map 裡，這裡只收集列號供提示用）。
            const guardedRows = [];
            setItems(prev => prev.map((item, itemIdx) => {
              const pid = typeof item.product === 'object' ? item.product?._id : item.product;
              const pd = productMap[pid] || null;
              // Backfill missing snapshot values from current product if quote item lacked them
              const productImage = item.productImage || pd?.productImage || '';
              const salesNotes = item.salesNotes || pd?.salesNotes || '';
              const vegetarianNote = item.vegetarianNote || pd?.vegetarianType || '';
              // 需求 R25：產品資訊欄改顯示「產品貨編」(productCode)；早期報價單未把 productCode 寫入 item 快照，
              // 這裡用實時產品資料補齊，避免舊單編輯時欄位空白。barcode 一併以實時資料補齊，以便顯示於規格欄位下方。
              const productCode = item.productCode || pd?.productCode || '';
              const barcode = item.barcode || pd?.barcode || '';
              // 需求 R121 R2：連 populate 都沒帶到（極舊單）時，用實時 Product 再補一次保存期限。
              const shelfLife = getItemShelfLife(item) || pd?.shelfLife || '';
              // 需求 R145 Q6=A：系列同理——快照 → populate → 實時 Product 三層，最後一層只服務
              // 「連 populate 都沒帶到」的極舊單（item.product 已被刪除或未關聯）。
              // 已有值就不覆蓋，確保 Q6=A「產品事後改系列不影響已建立的報價單」。
              const series = resolveItemSeries(item) || pd?.series || '';
              const defaultCostSource = item.defaultCostSource ?? (pd?.costSource || 'factory');
              const isCostSourceChanged = item.costSource
                ? item.costSource !== defaultCostSource
                : !!item.isCostSourceChanged;
              // 需求 R133 Q3=A：舊單保護逐列判定。此處是第一個「同時拿得到存檔 baseCost 與
              // 目前來源成本」的落點（來源成本要靠剛抓回來的 pd），故判定放在這裡而非上面
              // 那個 map。命中的列之後不會被任何自動重算改到單價（calcUnitPrice 早退）。
              const legacyCustomCostGuard = isLegacyCustomCostItem({
                item,
                sourceCost: getBaseCostFromSource(pd, item.costSource),
                savedBeforeFix: savedBeforeCustomCostFixRef.current,
              });
              if (legacyCustomCostGuard) guardedRows.push(itemIdx + 1);
              return {
                ...item,
                _productData: pd,
                _legacyCustomCostGuard: legacyCustomCostGuard,
                productCode,
                barcode,
                shelfLife,
                series,
                productImage,
                salesNotes,
                vegetarianNote,
                defaultCostSource,
                isCostSourceChanged,
                _origProductImage: productImage,
                _origEnglishName: item.englishName || '',
                // 需求 R118（Q8=A）：不再建立 _origSalesNotes 快照（上面 salesNotes 的帶入邏輯不變，AC8）。
                // 需求 R34（PM 2026-05-22 留言）：取得實時 Product 後，
                // 以 Product.vegetarianType 作為比對基準；若 Product 沒拿到則退回 vegetarianNote 本身。
                _origVegetarianType: pd?.vegetarianType ?? vegetarianNote,
              };
            }));
            // 需求 R133 Q3=A「提示」：命中就主動告知，不讓主管在毫不知情下覆核一個
            // 成本基準已經對不上的舊單。文案同時指出解法（重新輸入或解除保護）。
            if (guardedRows.length > 0) {
              pushToast(
                'error',
                `第 ${guardedRows.join('、')} 列可能是修復前遺失的自訂成本（存檔成本基準與來源成本不符），`
                + '已暫停這幾列的自動重算以免單價無聲變動。請在小眼睛重新輸入自訂成本，或按「改用來源成本重算」。'
              );
            }
          });
        }
        setTransactionNotes(q.transactionNotes || '');
        // 需求 R143（AC4）：備註回填。Q9=A 舊單沒有這個欄位 → undefined → 退回空字串，不做補寫。
        setInternalNotes(q.internalNotes || '');
        // 需求 R143 Q6=A：原始建單人。populate 失敗或舊資料無 createdBy 時留空，
        // 由畫面（AC15 的詳情頁同源寫法）顯示 '—'。
        setCreatedByName(q.createdBy?.name || '');
        setContactPerson(q.contactPerson?._id || q.contactPerson || '');
        if (q.updatedAt) setLastEditTime(new Date(q.updatedAt).toLocaleString('zh-TW'));
        setLoadingQuote(false);
      })
      .catch(err => {
        setError(err.message || '載入失敗');
        setLoadingQuote(false);
      });
    // 需求 R133：新增的 pushToast（舊單保護提示）是 useCallback([]) 的穩定參考，
    // 刻意不列入依賴——列進去不影響行為，但會讓這支「只在換單時重跑一次」的載入流程
    // 多一個看似可變的依賴，反而容易被日後重構誤讀。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id]);

  // Customer search debounce
  const searchCustomers = useCallback((kw) => {
    if (!kw.trim() || !token) { setCustomerSuggestions([]); setShowSuggestions(false); return; }
    fetch(`${API}/customers?keyword=${encodeURIComponent(kw)}&limit=10`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        const list = d.customers || d || [];
        setCustomerSuggestions(Array.isArray(list) ? list : []);
        setShowSuggestions(true);
      })
      .catch(() => {});
  }, [token]);

  const handleCustomerInputChange = (e) => {
    const val = e.target.value;
    setCustomerInput(val);
    setSelectedCustomer(null);
    if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
    customerDebounceRef.current = setTimeout(() => searchCustomers(val), 300);
  };

  const handleSelectCustomer = (cust) => {
    setSelectedCustomer(cust);
    setCustomerInput(cust.name);
    setShowSuggestions(false);
    setCustomerSuggestions([]);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // 需求 R97：自訂匯率優先於全域匯率（calcUnitPrice 算式不動，只是代入的 rate 換人，AC4）。
  const getCurrentRate = useCallback(() => {
    if (currency === 'TWD') return 1;
    if (customRate != null) return customRate;
    return getGlobalRate(currency, exchangeRates);
  }, [currency, customRate, exchangeRates]);

  const getLabelingFee = useCallback((mode) => {
    if (mode === 'none') return 0;
    if (mode === 'auto') {
      if (currency === 'TWD') return labelingFees['label_twd'] || 0;
      if (currency === 'HKD') return labelingFees['label_hkd'] || 0;
      if (currency === 'USD') return labelingFees['label_usd'] || 0;
      if (currency === 'EUR') return labelingFees['label_eur'] || 0;
    }
    return 0;
  }, [currency, labelingFees]);

  const recalcItem = useCallback((item) => {
    if (item.isManualPrice) return item;
    const rate = getCurrentRate();
    const labelFee = item.labelingMode === 'auto'
      ? getLabelingFee('auto')
      : (item.labelingMode === 'manual' ? (item.labelingFee || 0) : 0);
    const updatedItem = { ...item, labelingFee: labelFee };
    const newPrice = calcUnitPrice(updatedItem, rate, specialFormula, currency, globalMarkup);
    return { ...updatedItem, unitPrice: newPrice };
  }, [getCurrentRate, getLabelingFee, specialFormula, currency, globalMarkup]);

  // 需求 R97：customRate 一併列入依賴 → 上鎖套用後所有明細立即重算（AC2）；
  // recalcItem 開頭的 isManualPrice 早退保證手動單價列不被覆寫（AC3）。
  useEffect(() => {
    if (items.length === 0 || loadingQuote) return;
    setItems(prev => prev.map(item => recalcItem(item)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, specialFormula, exchangeRates, customRate, labelingFees, globalMarkup]);

  // 需求 R103（Q3=B / AC9）：載入時重算的階數必須連單價一起重跑，否則畫面會出現
  // 「階數已是新規則、單價還是存檔舊值」的不一致。上面那個 useEffect 只在 currency /
  // 匯率 / 加成等依賴變動時才觸發，報價單比匯率晚載入時不會被帶到，故另跑一次性重算。
  // 只跑一次（ref 把關）；isManualPrice 的列由 recalcItem 開頭早退保護，不會被覆寫。
  // 沒綁客戶 / 已審核的單 tierRecalcPendingRef 為 false ＝ 階數沒被動過，也就不重算單價。
  useEffect(() => {
    if (loadingQuote || !tierRecalcPendingRef.current || items.length === 0) return;
    tierRecalcPendingRef.current = false;
    setItems(prev => prev.map(item => recalcItem(item)));
  }, [loadingQuote, items.length, recalcItem]);

  // 需求 R103（Q5=A）：換客戶時逐列重刷紅點基準，與建立頁同一套規則。
  // 需求 R103（PM 2026-08-03 Q8=A / Q9）：改用 applyDefaultTier —— 非手動列的階數跟著新分級
  // 走並重算單價；手動列（Q8=A）保住使用者選的階數，只換比較基準。若只刷基準不動 priceTier，
  // 換完客戶會留下「階數還停在舊分級、單價也還是舊階數」的列，且因為它不是手動列，
  // Q9 的語意下不該亮紅點 → 變成無聲錯價。
  // 兩個不進場條件（缺一就會踩到 AC9 的兩個保護）：
  // ‧ 沒有分級（未選客戶／正在打字重搜客戶，selectedCustomer 被清成 null）：Q2=A 的預設是
  // 1 階，若照跑會把整單非手動列打成 1 階並跟著降價 —— 使用者只是還沒選完客戶。
  // 維持原值不動、基準也不刷，等真的選到客戶再一次算對。
  // ‧ 已審核唯讀單（isLocked）：載入時就約定不重算（tierRecalcPendingRef=false，AC9），
  // 這裡若補算，畫面價會偏離已核准的存檔價。
  useEffect(() => {
    if (!selectedCustomer?.level || isLocked) return;
    setItems(prev => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map(it => {
        const applied = applyDefaultTier(it, selectedCustomer?.level);
        if (applied.defaultPriceTier === it.defaultPriceTier
          && applied.priceTier === it.priceTier
          && applied.isManualPriceTier === !!it.isManualPriceTier) return it;
        changed = true;
        // 階數沒變就不必重算單價；isManualPrice 的列由 recalcItem 開頭早退保護（AC17）。
        return applied.priceTier === it.priceTier ? applied : recalcItem(applied);
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.level]);

  // 需求 R97：/ 切換（與 QuoteCreatePage 同一套規則）。
  // Q1=B：不做角色判斷；已審核鎖定（isLocked）時整組不可操作，由呼叫端的 render 條件把關（AC10）。
  const toggleRateLock = () => {
    if (isLocked) return;
    if (rateUnlocked) {
      const result = validateCustomRate(rateInput, currency, exchangeRates);
      if (!result.ok) { alert(result.message); return; } // 驗證未過維持解鎖，不套用（AC12）
      // 需求 R97 R2：若最終數值與該幣別的全域匯率完全相同（例如解鎖後未動就上鎖，或輸入
      // 與目前全域相同的值），視為「不自訂」→ 走 restoreGlobalRate 清狀態。否則會出現
      // isCustomExchangeRate=true 但值與全域相同的幽靈存檔，日後全域變動時反被凍住此舊值，
      // 且畫面會掛出無意義的紅色驚嘆號（Q3 語意上「!」是與全域不同的提示）。
      if (result.value === getGlobalRate(currency, exchangeRates)) {
        restoreGlobalRate();
        return;
      }
      setCustomRate(result.value);
      setRateUnlocked(false);
      return;
    }
    setRateInput(String(getCurrentRate()));
    setRateUnlocked(true);
  };

  // 需求 R97（Q3）：紅色驚嘆號比照單價那顆，點擊即還原成全域匯率。
  const restoreGlobalRate = () => {
    setCustomRate(null);
    setRateUnlocked(false);
    setRateInput('');
  };

  // 需求 R97（Q2=A）：切換報價幣別時清除自訂匯率並回到上鎖，改用新幣別的全域匯率。
  const handleCurrencyChange = (nextCurrency) => {
    setCurrency(nextCurrency);
    restoreGlobalRate();
  };

  // 需求 R90：把篩選條件（不含 page/limit）組成 params，供搜尋與載入更多共用
  //
  // 需求 R131（PM 2026-08-21 拍板）：
  // 「報價單新增／編輯的『新增產品』部分，篩選欄位皆用 OR。」Q4=A 含建立時間區間。
  // 本函式必須與 QuoteCreatePage.buildProductFilterParams 送出**完全相同**的 orFields
  //（AC10：同一組條件、同一筆資料，兩頁結果筆數必須相同）。改一邊就要改另一邊，
  // tests/productFilterSemantics.fixture.js 會靜態比對兩頁的字串是否一致。
  const buildProductFilterParams = () => {
    const params = new URLSearchParams();
    // 需求 R131：系列／類別／標籤／關鍵字／建立時間區間 五項併成同一包 OR（任一符合即列出）。
    // 下架排除（後端預設 productStatus $ne delisted）不在此清單內，永遠是頂層 AND（AC12）。
    params.set('orFields', 'series,category,tags,keyword,created');
    if (productKeyword) params.set('keyword', productKeyword);
    // 需求 R106：三組皆多選，逗號串送出；後端 buildProductListFilter 以 $in 精確比對（同欄任一符合）。
    if (productSeries.length > 0) params.set('series', productSeries.join(','));
    if (productCategory.length > 0) params.set('category', productCategory.join(','));
    if (productTags.length > 0) params.set('tags', productTags.join(','));
    // 需求 R123 Q2=B：只填一格也可搜尋（另一格為空即不送給後端）；後端 buildProductListFilter 已支援。
    // 需求 R131 Q4=A：本頁的這段區間會被併進上面那包 OR（其他頁仍是頂層 AND）。
    if (productCreatedFrom) params.set('createdFrom', productCreatedFrom);
    if (productCreatedTo) params.set('createdTo', productCreatedTo);
    return params;
  };

  // Product search（第一頁；重置分頁狀態）
  const handleProductSearch = async () => {
    if (!token) return;
    // 需求 R123 Q4=A：跨度驗證只在按下「搜尋」當下做（與建立頁一致）；失敗即中斷不打 API。
    const err = validateProductDateRange(productCreatedFrom, productCreatedTo);
    if (err) {
      setProductDateError(err);
      return;
    }
    setProductDateError('');
    setSearchLoading(true);
    const filter = buildProductFilterParams();
    lastSearchParamsRef.current = filter.toString();
    const params = new URLSearchParams(filter);
    params.set('limit', PRODUCT_PAGE_SIZE);
    params.set('page', 1);

    try {
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSearchResults(data.products || []);
      setSearchTotal(typeof data.total === 'number' ? data.total : (data.products?.length || 0));
      setSearchPage(1);
    } catch {
      setSearchResults([]);
      setSearchTotal(0);
      setSearchPage(1);
    } finally {
      setSearchLoading(false);
    }
  };

  // 需求 R90（移植 需求 R73 載入更多）：抓下一頁並 append，跨頁去重、保留既有勾選
  const handleLoadMoreProducts = async () => {
    if (!token || searchLoadingMore) return;
    const nextPage = searchPage + 1;
    setSearchLoadingMore(true);
    const params = new URLSearchParams(lastSearchParamsRef.current || '');
    params.set('limit', PRODUCT_PAGE_SIZE);
    params.set('page', nextPage);

    try {
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const incoming = Array.isArray(data.products) ? data.products : [];
      setSearchResults(prev => {
        const seen = new Set(prev.map(p => p._id));
        return [...prev, ...incoming.filter(p => !seen.has(p._id))];
      });
      if (typeof data.total === 'number') setSearchTotal(data.total);
      setSearchPage(nextPage);
    } catch {
      // 載入失敗維持現況，不清空已載入結果
    } finally {
      setSearchLoadingMore(false);
    }
  };

  const handleClearProductSearch = () => {
    setProductKeyword('');
    // 需求 R106 Q2=A：多選陣列改清空為 []（原本是清空成 ''，改陣列後會讓 buildProductFilterParams
    // 的 .length 存取直接爆）。
    setProductSeries([]);
    setProductCategory([]);
    setProductTags([]);
    // 需求 R123 Q3=B：清除同清空（Q1=B 預設也是空，所以「清空」＝回到預設）；同步清錯誤訊息。
    setProductCreatedFrom('');
    setProductCreatedTo('');
    setProductDateError('');
    setSearchResults([]);
    setSelectedProductIds(new Set());
    setSearchTotal(0);
    setSearchPage(1);
    lastSearchParamsRef.current = '';
  };

  const toggleProductTag = (value) => {
    setProductTags((prev) => prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]);
  };

  // 需求 R106 Q2=A：系列／類別多選切換（勾了再點＝取消），與建立頁 toggleProductSeries/Category 同語意。
  const toggleProductSeries = (value) => {
    setProductSeries((prev) => prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]);
  };
  const toggleProductCategory = (value) => {
    setProductCategory((prev) => prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]);
  };

  const toggleProductSelect = (pid) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  // 需求 R90 AC9（比照新增頁 需求 R73 / 需求 R62 全選 bug 教訓）：表頭全選＝「全選/取消 目前已載入的所有列」，
  // 用 every-id 成員判斷而非 size 相等，且只增減已載入列的 id，避免分頁 append 後誤判、誤清空其他頁勾選。
  const isAllLoadedSelected = searchResults.length > 0 && searchResults.every(p => selectedProductIds.has(p._id));

  const toggleSelectAll = () => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (isAllLoadedSelected) {
        searchResults.forEach(p => next.delete(p._id));
      } else {
        searchResults.forEach(p => next.add(p._id));
      }
      return next;
    });
  };

  // 需求 R136（PM 2026-08-24 拍板 Q5）：「已在報價清單中」的產品 id 集合。
  // 本票在編輯頁的真正根因就在這裡：舊寫法 `new Set(items.map(i => i.product))` 沒有做
  // id 正規化。本頁 items 來自 GET /quotes/:id，後端 quoteController 對 items.product 做了
  // populate → item.product 是**物件**，集合裡裝的是物件，`has(p._id)`（字串）恆為 false，
  // 重複判斷永遠不成立（本檔其他 8 處都有正規化，唯獨這裡漏掉）。
  // 改走 utils/quoteDuplicateItems 的 buildQuotedProductIdSet 統一正規化（AC3）。
  const quotedProductIds = buildQuotedProductIdSet(items);

  const handleAddProducts = () => {
    const toAdd = searchResults.filter(p => selectedProductIds.has(p._id));

    // 需求 R136（PM 2026-08-24 拍板 Q1=B 逐字：
    // 「照樣加入，允許同一產品在清單出現兩列，只是提醒。」）
    // 與建立頁同步移除 `.filter(p => !existingIds.has(p._id))`：重複品項照樣加入成第二列，
    // 提醒改由 Section 2 搜尋結果列事前呈現（quotedProductIds + DUPLICATE_ITEM_HINT），
    // 完全不擋、不中斷整批操作。兩頁行為必須一致（AC6），改一邊就要改另一邊。
    const newItems = toAdd
      .map(p => {
        // 需求 R103（AC4）：與建立頁一致，加入時即把 legacy 'distributor' 正規化為
        // 'distributor_0'，否則雙維查表與抽屜 radio 高亮都對不上。
        const costSource = normalizeCostSource(p.costSource || 'factory');
        // 需求 R103（AC5）：預設階數逐列判定（客戶分級 × 該列成本來源），
        // 同一張單內工廠列與經銷列可有不同階數。
        const defaultTier = getDefaultPriceTier(selectedCustomer?.level, costSource);
        const baseCost = getBaseCostFromSource(p, costSource);
        const labelingMode = p.canLabel ? 'auto' : 'none';
        const labelFee = labelingMode === 'auto' ? getLabelingFee('auto') : 0;

        const draft = {
          product: p._id,
          productCode: p.productCode || '',
          barcode: p.barcode || '',
          productName: p.productName || '',
          englishName: p.englishName || '',
          productImage: p.productImage || '',
          salesNotes: p.salesNotes || '',
          spec: p.spec || '',
          // 需求 R121 R2：與建立頁一致，保存期限跟著品項快照存下來，匯出不再依賴 populate。
          shelfLife: p.shelfLife || '',
          // 需求 R145 Q6=A：與建立頁一致，系列存成「加入當下」的快照（冷凍系列材積係數 23）。
          series: p.series || '',
          pcsPerBox: p.pcsPerBox || null,
          volumeCuft: p.volumeCuft || null,
          volumeMethod: p.volumeMethod || 'auto',
          priceTier: defaultTier,
          // 需求 R103（Q5=A / AC11）：本頁補上紅點比較基準，與建立頁同欄位名。
          defaultPriceTier: defaultTier,
          // 需求 R103（Q8=A）：新加入的品項預設非手動；使用者在 updateItem 手動改階數才置 true。
          isManualPriceTier: false,
          costSource,
          defaultCostSource: costSource,
          baseCost,
          // 需求 R170（PM 2026-09-15 Q1=A／Q3=A）：MOQ 與 baseCost 同源＝上面同一個 costSource，
          // 不再寫死 factoryCost；該來源沒填 MOQ 就空字串、不回退工廠。完整理由見建立頁同段。
          // Q1=A：只改「添加」入口，既有單載入時不重算 item.moq。
          moq: getCostEntryFromSource(p, costSource)?.moq || '',
          itemMarkup: 0,
          labelingMode,
          labelingFee: labelFee,
          unitPrice: null,
          isManualPrice: false,
          isCostSourceChanged: false,
          customCostEnabled: false,
          customCostValue: 0,
          // 需求 R149：新加入的品項兩種自訂成本都預設關閉。
          customBoxCostEnabled: false,
          customBoxCostValue: 0,
          quantity: 0,
          vegetarianNote: p.vegetarianType || '',
          notes: '',
          _productData: p,
          _origProductImage: p.productImage || '',
          _origEnglishName: p.englishName || '',
          // 需求 R118（Q8=A）：新加入品項同樣不建立 _origSalesNotes 快照。
          // 需求 R34（PM 2026-05-22 留言）：新加入品項的快照基準即 Product.vegetarianType。
          _origVegetarianType: p.vegetarianType || '',
        };

        const rate = getCurrentRate();
        draft.unitPrice = calcUnitPrice(draft, rate, specialFormula, currency, globalMarkup);
        return draft;
      });

    // 需求 R94（PM 2026-07-31 拍板 Q3/Q4）：
    // Q3「在『按下添加到報價單』當下重排一次」→ 追加時把既有品項與新品項合併後一併重排，
    // 結果與新增報價單完全一致（同一支 sortQuoteItems）。
    // Q4「存量資料：現行報價單不理」→ 只是開啟／匯出既有報價單時「不」自動重排
    // （載入處 :344 維持 DB 儲存順序原樣）；唯有使用者主動追加商品才會觸發本次重排。
    // Q5「維持這兩筆原本的前後順序」（PM 2026-07-31 拍板逐字）→ 既有品項若拿不到產品
    // 建立時間 createdAt（報價單快照沒存、_productData 尚未補抓回來），同貨編時不重排、
    // 維持原本的前後順序，不為此多打一次產品 API。規則實作見 utils/productSort.js。
    //
    // 需求 R114（報價單品項手動排序）評估結論＝不執行，故本行維持原樣（與 QuoteCreatePage 同步）：
    // 手動排序會與此處「合併後整份重排」衝突——排好之後再按一次「添加到報價單」即被洗掉。
    // PM 2026-08-18 留言逐字：「理解，這部分排序的確會與原本的設定衝突，
    // 本案不執行，人工丟到已取消」→ 兩頁都不加上移／下移、不加拖曳、不加手動排序旗標。
    setItems(prev => sortQuoteItems([...prev, ...newItems]));
    // 重排會改變品項索引，infoPanel 存的是「第幾列」→ 一併關閉，避免抽屜指到別的產品。
    setInfoPanel(null);
    setSelectedProductIds(new Set());
  };

  const updateItem = (index, field, value) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[index], [field]: value };

      // 需求 R103（Q8=A）：使用者手動改階數 → 記旗標，下次開編輯頁時 Q3=B 不覆寫這列。
      // 抽屜切成本來源不走本函式（走 handleCostSourceChange），該處自己覆寫階數與旗標
      // （需求 R104 Q1=A）；本函式只處理使用者在階數欄「重新選擇」的情形＝PM 說的驚嘆號來源。
      // Q9：applyDefaultTier 維護不變式，選回當下規則預設值時旗標歸零、紅點跟著熄。
      const patched = field === 'priceTier'
        ? applyDefaultTier(item, selectedCustomer?.level, value)
        : item;

      if (['priceTier', 'itemMarkup', 'labelingMode', 'labelingFee'].includes(field) && !patched.isManualPrice) {
        const labelFee = patched.labelingMode === 'auto'
          ? getLabelingFee('auto')
          : (patched.labelingMode === 'manual' ? (patched.labelingFee || 0) : 0);
        const itemWithFee = { ...patched, labelingFee: labelFee };
        const rate = getCurrentRate();
        itemWithFee.unitPrice = calcUnitPrice(itemWithFee, rate, specialFormula, currency, globalMarkup);
        next[index] = itemWithFee;
      } else {
        next[index] = patched;
      }
      return next;
    });
  };

  const handleManualPrice = (index, value) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], unitPrice: value === '' ? null : parseFloat(value) || 0, isManualPrice: true };
      return next;
    });
  };

  const handleRestoreAutoPrice = (index) => {
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[index], isManualPrice: false };
      const rate = getCurrentRate();
      item.unitPrice = calcUnitPrice(item, rate, specialFormula, currency, globalMarkup);
      next[index] = item;
      return next;
    });
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Sync inline-edited product field across all items sharing the same product id
  const syncProductFieldAcrossItems = useCallback((productId, field, value) => {
    if (!productId) return;
    setItems(prev => prev.map(it => {
      const itProductId = typeof it.product === 'object' ? it.product?._id : it.product;
      if (itProductId === productId) {
        return { ...it, [field]: value };
      }
      return it;
    }));
  }, []);

  // Trigger image upload for a specific item
  const triggerImageUpload = (idx) => {
    if (!canInlineEdit) return;
    const item = items[idx];
    const productId = typeof item.product === 'object' ? item.product?._id : item.product;
    if (!productId) {
      pushToast('error', '無法更新圖片：此品項缺少對應產品');
      return;
    }
    imageUploadCtxRef.current = { productId };
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
      imageInputRef.current.click();
    }
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    const ctx = imageUploadCtxRef.current;
    if (!file || !ctx?.productId || !token) return;
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API}/products/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || '圖片上傳失敗');
      }
      const { url } = await res.json();
      syncProductFieldAcrossItems(ctx.productId, 'productImage', url || '');
      pushToast('success', '圖片已更新（儲存報價單時將同步寫回產品）');
    } catch (err) {
      pushToast('error', err.message || '圖片上傳失敗');
    } finally {
      imageUploadCtxRef.current = null;
    }
  };

  // 需求 R101（PM 2026-08-03 拍板 Q1-Q5）：縮圖左下角截圖鈕上傳成功後的回填。
  // Q2=A 同一產品的所有列一起更新 → 走既有 syncProductFieldAcrossItems（與本頁檔案上傳同一條路徑）。
  // Q3=A 不立即 PUT 回 Product → 只改畫面 state，真正寫回發生在按「儲存」時的 writeBackProductFields()
  // （比對 _origProductImage）；這是編輯頁本來就有的模型，與建立頁語意不同。
  // Q4=A 顯示與檔案上傳一致的 toast，讓使用者知道還要按儲存才會寫回產品。
  const handleScreenshotUploaded = useCallback((productId, url) => {
    if (!productId) {
      pushToast('error', '無法更新圖片：此品項缺少對應產品');
      return;
    }
    syncProductFieldAcrossItems(productId, 'productImage', url || '');
    pushToast('success', '圖片已更新（儲存報價單時將同步寫回產品）');
  }, [syncProductFieldAcrossItems, pushToast]);

  const handleCostSourceChange = (index, newSource) => {
    // 需求 R133 Q3=A：切換成本來源是唯一會把 baseCost 從「存檔的自定成本」換成「來源成本」的
    // 路徑（下面那行 getEffectiveBaseCost），也就是舊單無聲改價的真正入口 → 保護中的列直接
    // 擋下並說明。不是靜靜地不做事：使用者按了 radio 卻沒反應會被當成壞掉。
    if (items[index]?._legacyCustomCostGuard) {
      pushToast(
        'error',
        '此列可能帶著修復前遺失的自訂成本，切換成本來源會讓單價無聲變動。'
        + '請先在下方重新輸入自訂成本，或按「改用來源成本重算」解除保護後再切換。'
      );
      return;
    }
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[index] };
      const product = item._productData;
      const defaultSource = item.defaultCostSource ?? (product?.costSource || 'factory');
      item.costSource = newSource;
      // 需求 R32：切換成本來源時，customCost 狀態保留，但需重新以 effectiveBaseCost 計算 baseCost。
      item.baseCost = getEffectiveBaseCost(item);
      item.isCostSourceChanged = newSource !== defaultSource;
      // 需求 R104 Q1=A（PM 2026-08-04，推翻 需求 R103 Q1=B）：切成本來源時階數一律覆寫成
      // 「客戶分級 × 新來源」的預設階，手動列一併覆寫、旗標歸零；覆寫後使用者再手動挑非預設
      // 階時紅點才重新亮。必須排在 item.costSource = newSource 之後（applyDefaultTier 讀
      // item.costSource）。與建立頁 QuoteCreatePage.js 同一段邏輯，完整理由寫在該檔。
      // Q4 保護：拿不到分級（未綁客戶／客戶欄打字中）不進場；已審核唯讀單（isLocked）
      // 本函式再擋一次——抽屜 radio 雖已 disabled，這裡不倚賴 UI 層把關。
      if (selectedCustomer?.level && !isLocked) {
        const tiered = applyDefaultTier(item, selectedCustomer.level, undefined, true);
        item.priceTier = tiered.priceTier;
        item.defaultPriceTier = tiered.defaultPriceTier;
        item.isManualPriceTier = tiered.isManualPriceTier;
      }
      // 需求 R104 Q2=A：新來源沒填 MOQ 時清空（原本 `if (newEntry?.moq)` 會沿用舊來源的 MOQ）。
      // 需求 R170（AC8）：取 entry 改走 quoteUnitPrice.js getCostEntryFromSource，與 handleAddProducts 同源。
      const newEntry = getCostEntryFromSource(product, newSource);
      item.moq = newEntry?.moq || '';
      const rate = getCurrentRate();
      if (!item.isManualPrice) {
        item.unitPrice = calcUnitPrice(item, rate, specialFormula, currency, globalMarkup);
      }
      next[index] = item;
      return next;
    });
  };

  // 需求 R32：自定成本開關／數值變動共用更新邏輯。
  const updateCustomCost = (index, patch) => {
    setItems(prev => {
      const next = [...prev];
      // 需求 R133 Q3=A：使用者親手動了自定成本＝已經明確表態，舊單保護的任務完成 → 解除，
      // 之後這列恢復正常重算（不解除的話 calcUnitPrice 會早退，新輸入的成本算不進單價）。
      const item = { ...next[index], ...patch, _legacyCustomCostGuard: false };
      item.baseCost = getEffectiveBaseCost(item);
      if (!item.isManualPrice) {
        const rate = getCurrentRate();
        item.unitPrice = calcUnitPrice(item, rate, specialFormula, currency, globalMarkup);
      }
      next[index] = item;
      return next;
    });
  };

  // 需求 R149 Q1=A（互斥）：勾其中一個自訂成本，另一個自動取消，同時只有一個生效。
  const handleCustomCostToggle = (index, enabled) => {
    // 需求 R133 Q1=B：handler 層防線，比照 handleCostSourceChange 不倚賴 UI 的 disabled。
    if (!canEditCustomCost) return;
    updateCustomCost(index, enabled
      ? { customCostEnabled: true, customBoxCostEnabled: false }
      : { customCostEnabled: false });
  };

  const handleCustomCostChange = (index, rawValue) => {
    if (!canEditCustomCost) return;
    if (rawValue === '' || rawValue == null) {
      updateCustomCost(index, { customCostValue: 0, _editingCustomCost: '' });
      return;
    }
    const n = Number(rawValue);
    if (!Number.isFinite(n) || n < 0) return;
    updateCustomCost(index, { customCostValue: n, _editingCustomCost: rawValue });
  };

  // 需求 R149：自訂箱成本。Q8=A —— 權限條件與自訂包成本**完全相同**（沿用 canEditCustomCost，
  // 待審核單主管可改、已審核一律不可改），避免做出後端會 403 的假編輯。
  // Q4=A —— 箱入數空／≤0 的品項換算不出包成本，handler 層一併擋下，不倚賴 UI 的 disabled。
  const handleCustomBoxCostToggle = (index, enabled) => {
    if (!canEditCustomCost) return;
    if (enabled && !canUseCustomBoxCost(items[index])) return;
    updateCustomCost(index, enabled
      ? { customBoxCostEnabled: true, customCostEnabled: false }
      : { customBoxCostEnabled: false });
  };

  const handleCustomBoxCostChange = (index, rawValue) => {
    if (!canEditCustomCost) return;
    if (rawValue === '' || rawValue == null) {
      updateCustomCost(index, { customBoxCostValue: 0, _editingCustomBoxCost: '' });
      return;
    }
    const n = Number(rawValue);
    if (!Number.isFinite(n) || n < 0) return;
    updateCustomCost(index, { customBoxCostValue: n, _editingCustomBoxCost: rawValue });
  };

  // 需求 R133 Q3=A：明確解除舊單保護＝「我知道原本的自定成本救不回來了，就用來源成本重算」。
  // 保護必須留一條明路，否則舊單會被凍在存檔價、連正常的階數／加成調整都反映不到單價。
  const clearLegacyCustomCostGuard = (index) => {
    if (!canEditCustomCost) return;
    setItems(prev => {
      const next = [...prev];
      const item = { ...next[index], _legacyCustomCostGuard: false };
      item.baseCost = getEffectiveBaseCost(item);
      if (!item.isManualPrice) {
        const rate = getCurrentRate();
        item.unitPrice = calcUnitPrice(item, rate, specialFormula, currency, globalMarkup);
      }
      next[index] = item;
      return next;
    });
    pushToast('success', '已解除保護，本列改用目前的成本來源重新計算單價。');
  };

  const setAllLabeling = (mode) => {
    setItems(prev => prev.map(item => {
      if (item.isManualPrice) return item;
      const labelFee = mode === 'auto' ? getLabelingFee('auto') : 0;
      const updated = { ...item, labelingMode: mode, labelingFee: labelFee };
      const rate = getCurrentRate();
      updated.unitPrice = calcUnitPrice(updated, rate, specialFormula, currency, globalMarkup);
      return updated;
    }));
  };

  const applyGlobalMarkup = (markupPct) => {
    setGlobalMarkup(markupPct);
  };

  const fetchLastTransaction = async () => {
    if (!selectedCustomer?._id || !token) return;
    try {
      const res = await fetch(
        `${API}/quotes?customer=${selectedCustomer._id}&limit=1&sort=-createdAt`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      const lastQuote = (data.quotes || [])[0];
      if (lastQuote?.transactionNotes) {
        setTransactionNotes(lastQuote.transactionNotes);
      }
    } catch {}
  };

  // Click-outside to close export menu
  useEffect(() => {
    const handler = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setShowExportMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Click-outside to close product tag / vegetarian dropdowns
  // 需求 R106：系列／類別多選下拉一併納入同一個 click-outside 監聽（與建立頁同步）。
  useEffect(() => {
    const handler = (e) => {
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(e.target)) {
        setTagsDropdownOpen(false);
      }
      if (seriesDropdownRef.current && !seriesDropdownRef.current.contains(e.target)) {
        setSeriesDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 需求 R37：PDF/Excel 統一使用共用 exporter（與 QuoteDetailPage 一致）。
  // 舊版 PDF 用 jsPDF → 中文亂碼；舊版 Excel 漏帶 barcode → 體積欄缺條碼。
  const buildExportQuoteObj = () => {
    const selectedSalesUser = salesUsers.find(u => u._id === contactPerson) || null;
    return {
      quoteNumber,
      companyHeader,
      quoteDate,
      expiryDate,
      currency,
      // 需求 R140 Q1=B（PM 2026-08-28）：匯出箱價要以「未進位單價」計算，而未進位原值只能由
      // 快照欄位重算（quoteUnitPrice.js resolveBoxUnitPriceBase）。本頁匯出走的是這支自組
      // 物件、沒有後端 populate，若不把單頭三個算式參數與品項的成本欄位帶上，重算會失敗
      // → 箱價就會靜靜退回舊規則（已進位單價 × 箱入數），與詳情頁匯出的同一張單不一致。
      exchangeRate: getCurrentRate(),
      specialFormula,
      globalMarkup: globalMarkup || 0,
      customerName: customerInput || selectedCustomer?.name || '',
      customer: selectedCustomer || null,
      contactPerson: selectedSalesUser ? {
        name: selectedSalesUser.name,
        phone: selectedSalesUser.phone,
        lineId: selectedSalesUser.lineId,
        email: selectedSalesUser.email,
      } : null,
      transactionNotes,
      // 需求 R143（PM 2026-08-29／AC6-AC7）： 這裡**刻意不帶** internalNotes（備註）。
      // 本函式組出來的物件是餵給 quotePdfExport／quoteExcelExport 的匯出來源，
      // PM 原始需求明寫「備註本欄在匯出報價單裡不要顯示，匯出報價單僅顯示交易條件」。
      // 雖然那兩支 exporter 目前也只讀 transactionNotes（雙重保險），
      // 但把備註帶進匯出物件等於在這裡埋一顆隨時會被印出來的地雷——不要補上去。
      // 建立者（createdBy）同理，Q7=A 拍板「不印」，兩支 exporter 也都沒有這個欄位。
      items: items.map(it => ({
        productCode: it.productCode,
        productName: it.productName,
        englishName: it.englishName,
        spec: it.spec,
        pcsPerBox: it.pcsPerBox,
        moq: it.moq,
        unitPrice: it.unitPrice,
        // 需求 R140 Q1=B：箱價的未進位單價基準所需的成本欄位（見上方 exchangeRate 註解）。
        // 這幾個值不會被匯出畫面印出來，只供 resolveBoxUnitPriceBase 重算 + 回頭核對；
        // 核對不過（例如手動改過單價）就退回 unitPrice，箱價維持舊規則。
        baseCost: it.baseCost ?? null,
        priceTier: it.priceTier,
        itemMarkup: it.itemMarkup || 0,
        labelingFee: it.labelingFee || 0,
        isManualPrice: !!it.isManualPrice,
        _legacyCustomCostGuard: !!it._legacyCustomCostGuard,
        volumeCuft: it.volumeCuft,
        volumeMethod: it.volumeMethod,
        // 需求 R145：resolveBoxUnitPriceBase 重算 raw 時要靠系列決定材積係數（冷凍 23／其餘 16）；
        // 本頁匯出走的是這支自組物件、沒有後端 populate，不帶就會拿 16 重算 → 與存值核對不符 →
        // 退回存值。功能上仍安全（箱價維持舊行為），但冷凍單會白白失去 Q1=B 的未進位精度。
        series: resolveItemSeries(it),
        barcode: it.barcode || '',
        // 需求 R121 R2（PM 2026-08-19 回報 QT-20260818-004 保存期限空白的直接根因）：
        // 本頁匯出走的是這支自組物件、沒有後端 populate，原本 product.shelfLife 讀的是
        // `it.shelfLife` —— 而 items state 從來沒有人寫過這個欄位，於是永遠是空字串。
        // 改用 getItemShelfLife 走「快照 → populate 產品 → 實時產品」三層 fallback，
        // 並同時放在 item 與 item.product 兩層，兩支匯出工具怎麼讀都拿得到。
        shelfLife: getItemShelfLife(it),
        // 需求 R96：未存檔直接匯出時也要帶出備註。匯出工具的「備註 Remark」欄改讀
        // vegetarianNote（葷素備註，上行）＋ salesNotes（業務備註，下行），這裡若不一起帶，
        // 本頁匯出仍會是空白。原本只帶的 it.notes 是已廢棄欄位（UI 無輸入元件、固定空字串），
        // 依 PM 拍板 Q5「不印」，故不再輸出。
        vegetarianNote: it.vegetarianNote || '',
        salesNotes: it.salesNotes || '',
        product: {
          productCode: it.productCode || '',
          barcode: it.barcode || '',
          productImage: it.productImage || '',
          shelfLife: getItemShelfLife(it),
          volumeMethod: it.volumeMethod,
          // 需求 R168 Q1=A：預購表的淨重／毛重從產品主檔即時讀，本頁只有 _productData 有這兩欄
          // （報價單品項快照刻意不存，見 Quote.js quoteItemSchema）。
          netWeight: it._productData?.netWeight ?? null,
          grossWeight: it._productData?.grossWeight ?? null,
        },
      })),
    };
  };

  // 需求 R119（Q7=A「凡列印/匯出成功都寫 exported=true」）：
  // 本頁匯出過去不寫旗標，導致「從編輯頁列印過的單子，列表頁『已列印/匯出』欄仍顯示 —」。
  // 作法與 QuoteDetailPage.markAsExported 相同：匯出成功後才打 PATCH，失敗只吞掉不擋使用者
  // （檔案已經下載了，旗標寫不進去不該跳錯）。
  const markAsExported = async () => {
    try {
      await fetch(`${API}/quotes/${id}/exported`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch {
      // Silently ignore — 匯出本身已成功
    }
  };

  const handleExportExcel = async () => {
    setShowExportMenu(false);
    try {
      const { exportQuoteExcel } = await import('../utils/quoteExcelExport');
      await exportQuoteExcel({ quote: buildExportQuoteObj() });
      await markAsExported();
    } catch (err) {
      setError('Excel 輸出失敗: ' + err.message);
    }
  };

  const handleExportPDF = async () => {
    setShowExportMenu(false);
    try {
      const { exportQuotePdf } = await import('../utils/quotePdfExport');
      await exportQuotePdf({ quote: buildExportQuoteObj() });
      await markAsExported();
    } catch (err) {
      setError('PDF 輸出失敗: ' + err.message);
    }
  };

  // 需求 R168（PM 2026-09-12 Q7=A）：預購表匯出成功同樣寫「已列印/匯出」旗標，比照現行兩種匯出。
  const handleExportPreorderExcel = async () => {
    setShowExportMenu(false);
    try {
      const { exportQuotePreorderExcel } = await import('../utils/quotePreorderExcelExport');
      await exportQuotePreorderExcel({ quote: buildExportQuoteObj() });
      await markAsExported();
    } catch (err) {
      setError('Excel 預購表輸出失敗: ' + err.message);
    }
  };

  // Save (PUT update)
  const handleSave = async (andSubmit = false) => {
    setError('');
    if (!quoteDate) { setError('請填寫報價日期'); return; }
    if (!expiryDate) { setError('請填寫截止日期'); return; }
    if (expiryDate < quoteDate) { setError('截止日期不能早於報價日期'); return; }
    if (!customerInput.trim()) { setError('請填寫客戶名稱'); return; }
    // 需求 R143 Q3=A：欄位標籤改為「交易條件」，錯誤訊息一併對齊（原為「請填寫交易條件備註」）。
    // 必填規則本身不變（AC3）。備註為 Q1=A 非必填、建立者不參與驗證（AC12），兩者皆不檢查。
    if (!transactionNotes.trim()) { setError('請填寫交易條件'); return; }
    if (!contactPerson) { setError('請選擇聯絡人 (業務)'); return; }

    // 需求 R133 Q3=A：仍有未處理的舊單保護列時，儲存前明確告知一次。
    // 為什麼非提示不可：存檔會把 updatedAt 推到修復界線之後，下次重開就不再判定為舊單
    // （旗標存的是 false、baseCost 還是那個對不上的數字），保護等於在這一刻失效。
    // 這裡只提示、不代替使用者決定要不要回填數值——PM Q2=A 已明示舊單不做回填。
    const guardedRowNumbers = items
      .map((it, idx) => (it._legacyCustomCostGuard ? idx + 1 : null))
      .filter(Boolean);
    if (guardedRowNumbers.length > 0) {
      const ok = window.confirm(
        `第 ${guardedRowNumbers.join('、')} 列仍是修復前遺失的自訂成本（存檔成本基準與來源成本不符）。\n`
        + '儲存後系統將不再標記這幾列，日後任何重算都會改用來源成本。\n\n'
        + '確定要直接儲存嗎？（建議先在小眼睛重新輸入自訂成本）'
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      const payload = {
        quoteDate,
        expiryDate,
        companyHeader,
        customer: selectedCustomer?._id || null,
        customerName: customerInput,
        currency,
        exchangeRate: getCurrentRate(),
        // 需求 R97（AC7 / Q5=A）：自訂匯率連同旗標一起存回，重新開啟編輯頁才還原得回來。
        isCustomExchangeRate: customRate != null,
        specialFormula,
        globalMarkup: globalMarkup || 0,
        items: items.map(item => ({
          product: item.product || null,
          productCode: item.productCode,
          barcode: item.barcode || '',
          productName: item.productName,
          englishName: item.englishName,
          productImage: item.productImage || '',
          salesNotes: item.salesNotes || '',
          spec: item.spec,
          // 需求 R121 R2：保存期限存回品項快照，既有舊單只要在本頁存過一次就補上了。
          shelfLife: getItemShelfLife(item),
          // 需求 R145 Q6=A：系列快照同樣存回，既有舊單在本頁存過一次就補上（之後不再依賴 populate）。
          series: resolveItemSeries(item),
          pcsPerBox: item.pcsPerBox,
          volumeCuft: item.volumeCuft,
          volumeMethod: item.volumeMethod || 'auto',
          priceTier: item.priceTier,
          // 需求 R103（Q8=A）：階數是否被人手改過要跟著存，否則下次重開時 Q3=B 會把它算掉。
          isManualPriceTier: !!item.isManualPriceTier,
          costSource: item.costSource,
          baseCost: item.baseCost,
          moq: item.moq,
          itemMarkup: item.itemMarkup,
          labelingMode: item.labelingMode,
          labelingFee: item.labelingFee,
          unitPrice: item.unitPrice,
          isManualPrice: item.isManualPrice,
          isCostSourceChanged: item.isCostSourceChanged,
          customCostEnabled: !!item.customCostEnabled,
          customCostValue: Number(item.customCostValue) || 0,
          // 需求 R149（AC14）：新欄位進存檔 payload。schema 已宣告，不會被 strict mode 丟棄。
          customBoxCostEnabled: !!item.customBoxCostEnabled,
          customBoxCostValue: Number(item.customBoxCostValue) || 0,
          quantity: item.quantity,
          vegetarianNote: item.vegetarianNote,
          notes: item.notes,
        })),
        transactionNotes,
        // 需求 R143（AC4）：備註隨單存檔。
        // 建立者不進 payload（AC12）——且後端 update 已明確剔除 req.body.createdBy，
        // 即使有人手動加進來也改不動原始建單人（AC14）。
        internalNotes,
        contactPerson: contactPerson || null,
      };

      const res = await fetch(`${API}/quotes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || '儲存失敗');
      }

      if (andSubmit) {
        // If pending quote edited by manager, submit means approve
        const newStatus = quoteStatus === 'pending' ? 'approved' : 'pending';
        const statusRes = await fetch(`${API}/quotes/${id}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status: newStatus }),
        });
        if (!statusRes.ok) {
          const err = await statusRes.json();
          throw new Error(err.message || (newStatus === 'approved' ? '審核失敗' : '送出審核失敗'));
        }
      }

      // After quote saved (and optional status change), write back changed product fields
      // (productImage / englishName / vegetarianType) independently — failures show toast but do not roll back.
      // 需求 R118：業務備註（salesNotes）已自回寫清單移除，故「只改業務備註」時 writes 為空、
      // 不會發出任何 PUT /products/:id，也不會跳「已回寫 N 筆產品資料」（AC7）。
      const wb = await writeBackProductFields();
      if (wb && wb.failures > 0) {
        setTimeout(() => navigate(`/quotes/${id}`), 2800);
      } else {
        navigate(`/quotes/${id}`);
      }
    } catch (err) {
      setError(err.message || '操作失敗');
    } finally {
      setSaving(false);
    }
  };

  // 需求 R162（PM 2026-09-06 Q1=A／Q2=A／Q6=A／Q7=A）：只儲存「備註」。
  // ‧ 走專用旁路 PATCH /api/quotes/:id/internal-notes，body 只帶 internalNotes 一個欄位；
  // 既有 PUT /api/quotes/:id 的 approved／pending 403 閘門完全不碰（AC8）。
  // ‧ 不呼叫 writeBackProductFields()、不改狀態、不送 items —— 畫面上其他欄位在唯讀頁本來
  // 就不可改，這裡也不把它們的值一起送出去（AC7 的前端側）。
  // ‧ AC14：**成功後不 navigate**。這顆鈕最主要的使用情境就是「在已審核單的檢視畫面上補一句
  // 內部備註」，存完把人踢回詳情頁等於打斷他的閱讀；改用既有 toast 回饋，失敗則顯示後端訊息。
  // ‧ Q6=A 不推進 updatedAt 由後端的 { timestamps: false } 保證，故這裡也**不**更新畫面上的
  // 「最後編輯」時間（lastEditTime）——顯示會跟著 DB 的 updatedAt 一起維持原值。
  const handleSaveNotes = async () => {
    setSavingNotes(true);
    setError('');
    try {
      const res = await fetch(`${API}/quotes/${id}/internal-notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ internalNotes }),
      });
      if (!res.ok) {
        // 非 2xx 時後端回 JSON（403 無權限／404 不存在／400 格式錯），以 catch 兜底非 JSON 回應。
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || '備註儲存失敗');
      }
      pushToast('success', '備註已儲存');
    } catch (err) {
      const msg = err.message || '備註儲存失敗';
      setError(msg);
      pushToast('error', msg);
    } finally {
      setSavingNotes(false);
    }
  };

  // 需求 R125：編輯頁「審核退回」——純狀態變更 pending → rejected，行為與詳情頁
  // QuoteDetailPage.handleStatusChange('rejected')（QuoteDetailPage.js:96-118、251）逐字一致：
  // 同一支 PATCH /api/quotes/:id/status、同樣不儲存畫面上未送出的編輯內容。
  // 「不順便儲存」是刻意的：退回代表這張單要交回業務修改，主管此刻在畫面上的臨時改動
  // 不應被寫進去；確認視窗會明講這件事，避免主管誤以為改動一併保存。
  // 成功後導向該報價單詳情頁 /quotes/:id（與 handleSave 成功後的導向一致）。
  const handleReject = async () => {
    setShowRejectModal(false);
    setRejecting(true);
    setError('');
    try {
      const res = await fetch(`${API}/quotes/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'rejected' }),
      });
      if (!res.ok) {
        // 後端非 2xx 時回的是 JSON（403「僅限主管操作」/ 400「無法從…轉換到…」），
        // 但仍以 catch 兜底，避免非 JSON 回應（例如 502）讓 res.json() 直接 throw 掉原始訊息。
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || '審核退回失敗');
      }
      navigate(`/quotes/${id}`);
    } catch (err) {
      setError(err.message || '審核退回失敗');
    } finally {
      setRejecting(false);
    }
  };

  const writeBackProductFields = async () => {
    if (!token) return { okCount: 0, failures: 0 };
    // Group by product id and pick latest values; compare to original snapshot
    const byProduct = new Map();
    for (const it of items) {
      const pid = typeof it.product === 'object' ? it.product?._id : it.product;
      if (!pid) continue;
      byProduct.set(pid, it);
    }
    const writes = [];
    byProduct.forEach((it, pid) => {
      const updates = {};
      if ((it.productImage || '') !== (it._origProductImage || '')) updates.productImage = it.productImage || '';
      if ((it.englishName || '') !== (it._origEnglishName || '')) updates.englishName = it.englishName || '';
      // 需求 R118（PM 2026-08-18 拍板 Q1=B「兩頁一起停」、Q2「只停業務備註一欄」）：
      // 業務備註（salesNotes）不再回寫 Product.salesNotes——這裡「刻意」沒有 updates.salesNotes。
      // Why: 報價單上的業務備註是「這一張單」的說明，不該把產品主檔一起改掉。
      // （型錄管理→批量修改的回寫路徑 CatalogPage.handleSaveBatchEdit → POST /products/batch-update
      // 不受影響，仍照舊回寫，見 CatalogPage.js:85 writeBack: true。）
      // Q3=A: 值仍存在報價單上（payload 的 items[].salesNotes，見 :1241 / :1304），
      // 重開編輯頁／詳情頁／PDF・Excel 匯出照樣看得到使用者改的值。
      // 請勿再把 salesNotes 加回來——tests/quoteSalesNotesNoWriteBack.fixture.js 會擋。
      // 需求 R34（PM 2026-05-22 留言）：「此葷素備註，經修改儲存後，回寫 product」
      // 編輯頁儲存時若 vegetarianNote 與初始 Product.vegetarianType 不同，回寫到 Product.vegetarianType。
      if ((it.vegetarianNote || '') !== (it._origVegetarianType || '')) updates.vegetarianType = it.vegetarianNote || '';
      if (Object.keys(updates).length > 0) writes.push({ pid, updates, label: it.productCode || it.productName || pid });
    });
    if (writes.length === 0) return { okCount: 0, failures: 0 };
    const results = await Promise.allSettled(writes.map(async ({ pid, updates, label }) => {
      const res = await fetch(`${API}/products/${pid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = res.status === 403
          ? `報價單已儲存但產品回寫失敗（無權限）：${label}`
          : `報價單已儲存但產品回寫失敗（${label}）：${data.message || res.status}`;
        throw new Error(msg);
      }
      return label;
    }));
    let okCount = 0;
    let failures = 0;
    results.forEach(r => {
      if (r.status === 'fulfilled') {
        okCount += 1;
      } else {
        failures += 1;
        pushToast('error', r.reason?.message || '產品回寫失敗');
      }
    });
    if (okCount > 0) pushToast('success', `已回寫 ${okCount} 筆產品資料`);
    return { okCount, failures };
  };

  const currentRate = getCurrentRate();
  // 需求 R97：全域匯率（驚嘆號 tooltip 用）與「此單是否為自訂匯率」。
  const globalRate = getGlobalRate(currency, exchangeRates);
  const isCustomRate = customRate != null;

  if (!user) return null;

  if (loadingQuote) {
    return (
      <div className="qc-page">
        <Header title={isLocked ? '檢視報價單' : '編輯報價單'} user={user} onLogout={handleLogout} onUserUpdate={setUser} />
        <div className="qc-container">
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--color-text-secondary)' }}>載入中…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="qc-page">
      <Header title={isLocked ? '檢視報價單' : '編輯報價單'} user={user} onLogout={handleLogout} onUserUpdate={setUser} />

      <div className="qc-container">
        {error && <div className="qc-error-banner">{error}</div>}

        {/* ========== SECTION 1: 基本資訊 ========== */}
        <section className="qc-section">
          <div className="qc-section-header">
            <span className="qc-section-number">1</span>
            報價單基本資訊
          </div>
          <div className="qc-section-body">
            {/* Row 1: 4 columns */}
            <div className="qc-form-grid qc-grid-4" style={{ marginBottom: 'var(--spacing-md)' }}>
              <div className="form-group">
                <label>報價單編號</label>
                <input className="form-control" value={quoteNumber} readOnly />
              </div>
              <div className="form-group">
                <label>報價日期 <span className="qc-required">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  value={quoteDate}
                  onChange={e => setQuoteDate(e.target.value)}
                  disabled={isLocked}
                />
              </div>
              <div className="form-group">
                <label>截止日期 <span className="qc-required">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  value={expiryDate}
                  min={quoteDate}
                  onChange={e => setExpiryDate(e.target.value)}
                  disabled={isLocked}
                />
              </div>
              <div className="form-group">
                <label>公司抬頭 <span className="qc-required">*</span></label>
                <select
                  className="form-control"
                  value={companyHeader}
                  onChange={e => setCompanyHeader(e.target.value)}
                  disabled={isLocked}
                >
                  {companyOptions.map(c => (
                    <option key={c._id} value={c.label}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* Row 2: 3 columns */}
            <div className="qc-form-grid qc-grid-3">
              <div className="form-group" ref={suggestionsRef} style={{ position: 'relative' }}>
                <label>客戶名稱 <span className="qc-required">*</span></label>
                {/*
                  需求 R113：與 QuoteCreatePage.js 是兩份獨立程式碼，文案必須同步改（只改一頁視為未完成）。
                  本欄與客戶管理共用 GET /api/customers ＋ buildCustomerFilter 的五欄 $or
                  （name／customerNumber／shortName／contacts.name／contacts.email），搜尋範圍本來就一致。
                  PM 2026-08-18 拍 Q1=A「完全比照客戶管理」、Q5「只做 D1（文案）即可結案」，
                  故文案與 CustomersPage.js:286 逐字相同；手填客戶名稱的既有能力不受影響。
                */}
                <input
                  type="text"
                  className="form-control"
                  placeholder="輸入客戶名稱、編號、簡稱、聯絡人、Email..."
                  value={customerInput}
                  onChange={handleCustomerInputChange}
                  onFocus={() => customerSuggestions.length > 0 && setShowSuggestions(true)}
                  autoComplete="off"
                  disabled={isLocked}
                />
                {showSuggestions && customerSuggestions.length > 0 && (
                  <div className="qc-suggestions">
                    {customerSuggestions.map(c => (
                      <div
                        key={c._id}
                        className="qc-suggestion-item"
                        onMouseDown={() => handleSelectCustomer(c)}
                      >
                        <span className="qc-suggestion-name">{c.name}</span>
                        {c.level && <span className={`qc-level-badge qc-level-${c.level.toLowerCase()}`}>{c.level}</span>}
                        {c.country && <span className="qc-suggestion-meta">{c.country}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {selectedCustomer && (
                  <div className="qc-selected-customer">
                    <span>已選: <strong>{selectedCustomer.name}</strong></span>
                    {selectedCustomer.level && (
                      <span className={`qc-level-badge qc-level-${selectedCustomer.level.toLowerCase()}`}>
                        {selectedCustomer.level}級
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>報價幣別</label>
                <select
                  className="form-control"
                  value={currency}
                  onChange={e => handleCurrencyChange(e.target.value)}
                  disabled={isLocked}
                >
                  <option value="TWD">TWD — 新台幣</option>
                  <option value="HKD">HKD — 港幣</option>
                  <option value="USD">USD — 美元</option>
                  <option value="EUR">EUR — 歐元</option>
                </select>
                {/* 需求 R97：與 QuoteCreatePage 同一份結構。已審核鎖定（isLocked）時  不可點、
                    不顯示輸入框，只保留唯讀數值與自訂標記（AC10）。 */}
                {currency !== 'TWD' && (
                  <div className="exchange-rate-display">
                    <span className="rate-label">當前匯率:</span>
                    {rateUnlocked && !isLocked ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control form-control-sm rate-input"
                        value={rateInput}
                        onChange={e => { if (isRateInputAllowed(e.target.value)) setRateInput(e.target.value); }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); toggleRateLock(); } }}
                        title={`最多 ${getRateDecimals(currency, exchangeRates)} 位小數，須大於 0`}
                        autoFocus
                      />
                    ) : (
                      <span className="rate-value">{currentRate || '(未設定)'}</span>
                    )}
                    {isCustomRate && !rateUnlocked && (
                      <span
                        className="price-modified-dot"
                        title={isLocked
                          ? '此報價單使用自訂匯率'
                          : `此報價單使用自訂匯率（全域匯率為 ${globalRate}），點擊還原`}
                        onClick={() => { if (!isLocked) restoreGlobalRate(); }}
                      >!</span>
                    )}
                    <span
                      className={`lock-icon${isLocked ? '' : ' rate-lock-toggle'}`}
                      role={isLocked ? undefined : 'button'}
                      title={isLocked
                        ? '已審核鎖定，匯率不可修改'
                        : (rateUnlocked ? '點擊上鎖並套用此匯率' : '點擊解鎖以自訂此報價單的匯率')}
                      onClick={toggleRateLock}
                    >{rateUnlocked && !isLocked ? '🔓' : '🔒'}</span>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>特殊公式</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="例如 *1.03/3.8 （依序套用）"
                  value={specialFormula}
                  onChange={e => setSpecialFormula(e.target.value)}
                  disabled={isLocked}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========== SECTION 2: 產品選取（需求 R33：已審核狀態整段隱藏）========== */}
        {!isLocked && (
        <section className="qc-section">
          <div
            className="qc-section-header qc-section-header--collapsible"
            role="button"
            tabIndex={0}
            aria-expanded={!productPickerCollapsed}
            onClick={() => setProductPickerCollapsed(v => !v)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setProductPickerCollapsed(v => !v);
              }
            }}
          >
            <div className="qc-section-header-left">
              <span className="qc-section-number">2</span>
              產品選取
            </div>
            <div className="qc-section-header-right">
              <span className="qc-collapse-toggle">
                <span className="qc-collapse-arrow">{productPickerCollapsed ? '▸' : '▾'}</span>
                {productPickerCollapsed ? '展開' : '收合'}
              </span>
            </div>
          </div>
          <div
            className="qc-section-body"
            style={productPickerCollapsed ? { display: 'none' } : undefined}
          >
            <div className="qc-search-bar">
              {/* 需求 R29（PM 2026-05-22）：編輯頁同步建立頁的兩列佈局 —
                  第一列為系列 / 類別 / 關鍵字檢索；第二列放產品標籤、葷素備註與搜尋/清除按鈕。 */}
              {/* 需求 R107（PM 2026-08-04）：與建立頁同步把第一列調成產品管理的
                  順序 系列 → 類別 → 關鍵字檢索，並把標籤「關鍵字」改為「關鍵字檢索」。
                  純節點順序與文案調整，不動 state / query / 篩選行為。 */}
              <div className="qc-search-row">
                {/* 需求 R106 R6（PM 2026-08-04：「報價單管理的產品選取的選擇方式也
                    依樣改成相同的呈現方式，保持一致性與方便使用性」）：系列／類別改用與型錄管理
                    相同的 .multi-select-*（白底 trigger + 已選 chip + ▲/▼ + 單欄 checkbox 清單），
                    取代 R3 沿用的標籤用 .tag-dropdown（多欄 grid，在這三欄並排的窄欄位會溢出）。
                    樣式來源：styles/multiSelectDropdown.css（四頁共用）。
                    Q1=A：三組之間是「任一符合」，多勾系列不會再把類別的結果一起縮小。
                    與建立頁 (QuoteCreatePage.js) JSX 完全對齊。 */}
                <div className="form-group" ref={seriesDropdownRef}>
                  <label>系列 (可複選)</label>
                  <div className="multi-select-dropdown">
                    <div
                      className="multi-select-trigger"
                      onClick={() => setSeriesDropdownOpen(prev => !prev)}
                    >
                      <div className="selected-tags">
                        {productSeries.length === 0
                          ? <span className="placeholder-text">全部系列</span>
                          : productSeries.map(s => (
                            <span key={s} className="selected-tag">
                              {s}
                              <button className="tag-remove-btn" onClick={e => { e.stopPropagation(); toggleProductSeries(s); }}>×</button>
                            </span>
                          ))
                        }
                      </div>
                      <span className="arrow">{seriesDropdownOpen ? '▲' : '▼'}</span>
                    </div>
                    {seriesDropdownOpen && (
                      <div className="multi-select-menu">
                        {seriesOptions.map(s => (
                          <label key={s} className="multi-select-option">
                            <input
                              type="checkbox"
                              checked={productSeries.includes(s)}
                              onChange={() => toggleProductSeries(s)}
                            />
                            {s}
                          </label>
                        ))}
                        {seriesOptions.length === 0 && (
                          <div className="multi-select-empty">尚無系列</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group" ref={categoryDropdownRef}>
                  <label>類別 (可複選)</label>
                  <div className="multi-select-dropdown">
                    <div
                      className="multi-select-trigger"
                      onClick={() => setCategoryDropdownOpen(prev => !prev)}
                    >
                      <div className="selected-tags">
                        {productCategory.length === 0
                          ? <span className="placeholder-text">全部類別</span>
                          : productCategory.map(c => (
                            <span key={c} className="selected-tag">
                              {c}
                              <button className="tag-remove-btn" onClick={e => { e.stopPropagation(); toggleProductCategory(c); }}>×</button>
                            </span>
                          ))
                        }
                      </div>
                      <span className="arrow">{categoryDropdownOpen ? '▲' : '▼'}</span>
                    </div>
                    {categoryDropdownOpen && (
                      <div className="multi-select-menu">
                        {categoryOptions.map(c => (
                          <label key={c} className="multi-select-option">
                            <input
                              type="checkbox"
                              checked={productCategory.includes(c)}
                              onChange={() => toggleProductCategory(c)}
                            />
                            {c}
                          </label>
                        ))}
                        {categoryOptions.length === 0 && (
                          <div className="multi-select-empty">尚無類別</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group qc-search-keyword">
                  <label>關鍵字檢索</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="產品編號、品名或條碼…"
                    value={productKeyword}
                    onChange={e => setProductKeyword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleProductSearch()}
                  />
                </div>
              </div>
              <div className="qc-search-row">
                <div className="form-group" ref={tagsDropdownRef}>
                  <label>產品標籤</label>
                  <div className={`tag-dropdown${tagsDropdownOpen ? ' open' : ''}`}>
                    <div
                      className="tag-dropdown-trigger form-control"
                      onClick={() => setTagsDropdownOpen(prev => !prev)}
                      style={{ flexWrap: 'wrap', gap: '4px', height: 'auto', minHeight: '36px' }}
                    >
                      {productTags.length === 0 ? (
                        <span className="tag-dropdown-placeholder">全部（可複選）</span>
                      ) : (
                        productTags.map(tag => (
                          <span key={tag} className="tag" onClick={e => e.stopPropagation()}>
                            {tag}
                            <button className="tag-remove" onClick={e => { e.stopPropagation(); toggleProductTag(tag); }}>×</button>
                          </span>
                        ))
                      )}
                      <span className="tag-dropdown-arrow" style={{ marginLeft: 'auto' }}>▾</span>
                    </div>
                    <div className="tag-dropdown-menu">
                      {tagOptions.map(opt => (
                        <label key={opt._id} className="tag-dropdown-item">
                          <input
                            type="checkbox"
                            checked={productTags.includes(opt.value)}
                            onChange={() => toggleProductTag(opt.value)}
                          />
                          {opt.value}
                        </label>
                      ))}
                      {tagOptions.length === 0 && (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-sm)' }}>
                          尚無標籤
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* 需求 R59 D5(A)：篩選面板 vegetarianType 已移除；如需搜尋含特定字樣，改用 keyword 全文搜尋。 */}
                {/* 需求 R123（PM 2026-08-19 拍板 Q1=B/Q2=B/Q3=B/Q4=A/Q5=C）：與建立頁 JSX 完全對齊。
                    位置在「產品標籤」右側、「搜尋／清除」按鈕左側；Q5=C 由 .qc-search-row 現有的
                    flex-wrap 決定換行，兩頁是兩份獨立 code，改一邊必須同時改另一邊（需求 R90 教訓）。 */}
                <div className="form-group qc-search-date-range">
                  <div className="new-product-date-range">
                    <label className="catalog-filter-label">建立時間區間（最長 2 個月）</label>
                    <div className="date-range-inputs">
                      <input
                        type="date"
                        className="date-input"
                        value={productCreatedFrom}
                        onChange={e => { setProductCreatedFrom(e.target.value); setProductDateError(''); }}
                      />
                      <span className="date-range-sep">～</span>
                      <input
                        type="date"
                        className="date-input"
                        value={productCreatedTo}
                        onChange={e => { setProductCreatedTo(e.target.value); setProductDateError(''); }}
                      />
                    </div>
                    {productDateError && <div className="date-range-error">{productDateError}</div>}
                  </div>
                </div>
                <div className="qc-search-actions">
                  <button className="qc-btn qc-btn-primary" onClick={handleProductSearch} disabled={searchLoading}>
                    {searchLoading ? '搜尋中…' : '搜尋'}
                  </button>
                  <button className="qc-btn qc-btn-secondary" onClick={handleClearProductSearch}>清除</button>
                </div>
              </div>
            </div>

            {searchResults.length > 0 && (
              <>
                <div className="qc-search-results-header">
                  {/* 需求 R90：原本「找到 N 筆產品」把本頁筆數誤述成總筆數，改為與新增頁一致的「已顯示 X / 共 Y 筆」 */}
                  <span>已顯示 {searchResults.length} / 共 {searchTotal} 筆</span>
                  <button className="qc-btn qc-btn-success" onClick={handleAddProducts} disabled={selectedProductIds.size === 0}>
                    新增產品 ({selectedProductIds.size})
                  </button>
                </div>
                <div className="qc-table-wrap">
                  <table className="qc-table qc-search-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>
                          <input
                            type="checkbox"
                            className="select-checkbox"
                            checked={isAllLoadedSelected}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th>產品編號</th>
                        <th>中文品名</th>
                        <th>英文品名</th>
                        <th>規格</th>
                        <th>箱入數</th>
                        <th>成本來源</th>
                        {/* 需求 R136 Q5：列尾的重複提醒欄；沒有重複時整欄空白，版面不受影響。 */}
                        <th className="qc-dup-col" aria-label="重複提醒" />
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map(p => {
                        // 需求 R136（Q5）：事前提醒——此產品已在下方報價品項清單中。
                        const inQuote = isProductInQuote(quotedProductIds, p);
                        return (
                        <tr
                          key={p._id}
                          className={[
                            selectedProductIds.has(p._id) ? 'qc-row-selected' : '',
                            inQuote ? 'qc-row-in-quote' : '',
                          ].filter(Boolean).join(' ')}
                          onClick={() => toggleProductSelect(p._id)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="select-checkbox"
                              checked={selectedProductIds.has(p._id)}
                              onChange={() => toggleProductSelect(p._id)}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                          <td className="product-code">{p.productCode}</td>
                          <td>{p.productName}</td>
                          <td>{p.englishName}</td>
                          <td>{p.spec}</td>
                          <td>{p.pcsPerBox ?? '—'}</td>
                          <td>
                            <span className="qc-cost-source-tag">{p.costSource || 'factory'}</span>
                          </td>
                          <td className="qc-dup-col">
                            {inQuote && <span className="qc-dup-tag">{DUPLICATE_ITEM_HINT}</span>}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* 需求 R90：載入更多（逐頁 append＋_id 去重；載完 X >= Y 時自動隱藏） */}
                {searchResults.length < searchTotal && (
                  <div style={{ textAlign: 'center', margin: 'var(--spacing-sm) 0' }}>
                    <button
                      className="qc-btn qc-btn-secondary"
                      onClick={handleLoadMoreProducts}
                      disabled={searchLoadingMore}
                    >
                      {searchLoadingMore ? '載入中…' : `載入更多（已顯示 ${searchResults.length} / 共 ${searchTotal} 筆）`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
        )}

        {/* ========== SECTION 3: 報價品項清單 ========== */}
        <section className="qc-section">
          <div className="qc-section-header">
            <div className="qc-section-header-left">
              <span className="qc-section-number">3</span>
              報價品項清單
              <span className="qc-item-count">（{items.length} 項）</span>
            </div>
            <div className="qc-section-header-right qc-section3-controls">
              <label className="qc-inline-label">全單加成 (%)</label>
              {/*
                需求 R126（PM 2026-08-21）：附件紅框指的就是本欄——「這欄位只能填整數，
                  無法填有小數點，例1.5」。真因是受控數字 state 往返吃掉中間態（打「1.」→ parseFloat=1
                  → 回填「1」小數點被抹掉；打「0」→ 因 `=== 0 ? ''` 整欄被清空），非驗證規則。
                  修法沿用 需求 R84 範式：原始字串 buffer + regex 限位 + onBlur 清 buffer。
                  Q3＝不支援負數（regex 不含負號，舊 `v === '-'` 分支移除）；
                  Q5＝打幾位存幾位不四捨五入（此處只 parseFloat，不做 toFixed／Math.round）；
                  Q1/Q2/Q4 PM 未填 → 位數上限暫 3 位（GLOBAL_MARKUP_INPUT_RE）、兩頁同步、0 仍顯示空白。
                  ※ 本檔與 QuoteCreatePage.js 是兩份獨立程式碼，此輸入框兩邊必須同步修改。
              */}
              <input
                type="text"
                inputMode="decimal"
                className="form-control form-control-sm qc-markup-input"
                value={editingGlobalMarkup != null ? editingGlobalMarkup : (globalMarkup === 0 ? '' : globalMarkup)}
                style={{ width: 80 }}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '' || GLOBAL_MARKUP_INPUT_RE.test(v)) {
                    setEditingGlobalMarkup(v);
                    const n = (v === '' || v === '.') ? 0 : parseFloat(v);
                    applyGlobalMarkup(isNaN(n) ? 0 : n);
                  }
                }}
                onBlur={() => setEditingGlobalMarkup(null)}
                disabled={isLocked}
              />
              <button className="qc-btn qc-btn-outline qc-btn-sm" onClick={() => setAllLabeling('auto')} disabled={isLocked}>
                一鍵全選貼標
              </button>
              <button className="qc-btn qc-btn-outline qc-btn-sm" onClick={() => setAllLabeling('none')} disabled={isLocked}>
                一鍵全選取消貼標
              </button>
            </div>
          </div>
          <div className="qc-section-body">
            {items.length === 0 ? (
              <div className="qc-items-empty">尚未加入任何產品，請在上方搜尋並新增。</div>
            ) : (
              /* 需求 R137：PM 2026-08-24 拍板「分頁並非合適此頁面的做法，此案先暫停」；
                 2026-08-27 再拍「維持現況，但讓可見品項範圍變大一些／把 section 高度加高」→
                 編輯頁與新增頁共用 .qc-items-table-wrap，同樣維持整份 items 一次渲染、不加分頁，
                 只在 CSS 把該容器的 max-height 從 55vh 放寬成 max(520px, calc(100vh - 180px))。
                 下方 items.map 的 idx 是絕對索引，切勿改成 items.slice(...).map(...)。
                 算式、量測與風險詳見 QuoteCreatePage.css 的 Items Table 段註解。 */
              <div className="qc-items-table-wrap">
                <table className="qc-table qc-items-table">
                  <thead>
                    <tr>
                      <th style={{ width: 280 }}>產品資訊</th>
                      <th>規格/箱入</th>
                      <th>
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                          <span>價格指數</span>
                          <span>MOQ</span>
                        </div>
                      </th>
                      <th>
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                          <span>單品加價</span>
                          <span>貼標費用</span>
                        </div>
                      </th>
                      <th style={{ width: 120 }}>單價</th>
                      <th style={{ width: 80 }}>數量</th>
                      {/* 需求 R118（Q6）：業務備註停止回寫，表頭說明同步改成「僅套用於本張報價單」。 */}
                      <th title="葷素備註：自動帶入產品詳細頁的「葷素類別」(Product.vegetarianType)，可手動修改，儲存時回寫產品；業務備註：自動帶入產品詳細頁的「業務備註」(Product.salesNotes)，修改後僅套用於本張報價單，不回寫產品。">
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                          <span>葷素備註</span>
                          <span>業務備註</span>
                        </div>
                      </th>
                      <th style={{ width: 80 }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      // 需求 R32：自定成本啟用、值為有限正數，且與目前來源預設不同時，視為「已修改」需顯示警示。
                      // 加入 isFinite 防護避免 Infinity / NaN 觸發誤警示。
                      const sourceCostForRow = getBaseCostFromSource(item._productData, item.costSource);
                      const customCostNumRow = Number(item.customCostValue);
                      const isCustomCostActive =
                        item.customCostEnabled
                        && Number.isFinite(customCostNumRow)
                        && customCostNumRow > 0
                        && customCostNumRow !== Number(sourceCostForRow);
                      // 需求 R149（AC15）：自訂箱成本換算出的包成本與來源成本不同時，同樣亮紅點。
                      // 比較的是**換算後的包成本**，不是使用者填的箱成本（量級不同，直接比會整排誤報）。
                      const boxPackCostRow = packCostFromCustomBoxCost(item.customBoxCostValue, item.pcsPerBox);
                      const isCustomBoxCostActive =
                        item.customBoxCostEnabled
                        && boxPackCostRow != null
                        && boxPackCostRow !== Number(sourceCostForRow);
                      // 需求 R103（Q5=A / AC11）：本頁補上建立頁已有的「價格階非預設」紅點理由。
                      // 基準 defaultPriceTier 在載入既有單 / 加入商品 / 換客戶時同步維護；
                      // 缺值（例如尚未重新載入的舊 state）時回推當下規則值，避免整排誤報。
                      const rowDefaultTier = item.defaultPriceTier
                        ?? getDefaultPriceTier(selectedCustomer?.level, item.costSource);
                      const isPriceTierChanged = Number(item.priceTier) !== Number(rowDefaultTier);
                      return (
                      <React.Fragment key={idx}>
                      <tr className={item.isManualPrice || item.isCostSourceChanged || isCustomCostActive || isCustomBoxCostActive || isPriceTierChanged ? 'qc-row-modified' : ''}>
                        <td>
                          <div className="product-info-cell">
                            <div
                              className={`product-thumb-wrapper product-image-download-host${canInlineEdit ? ' product-thumb-editable' : ''}`}
                              onClick={canInlineEdit ? () => triggerImageUpload(idx) : undefined}
                              title={canInlineEdit ? '點擊更新產品圖片' : ''}
                            >
                              {item.productImage ? (
                                <img
                                  className="product-thumb"
                                  src={item.productImage}
                                  alt={item.productName}
                                />
                              ) : (
                                <span className="no-image-badge">無圖片</span>
                              )}
                              {canInlineEdit && (
                                <span className="product-thumb-edit-overlay">更換圖片</span>
                              )}
                              {/* 需求 R101：圖片框左下角截圖小圖示（絕對定位、不佔版面、不改變縮圖寬高），
                                  與建立頁 QuoteCreatePage.js 同位置、同寫法，沿用 QuoteCreatePage.css 既有的
                                  .product-thumb-wrapper .product-thumb-screenshot-btn（本頁第 14 行已 import 該 CSS）。
                                  PM Q1：「一但可編輯，就是要有此功能」→ 條件與同框「更換圖片」overlay 一致綁 canInlineEdit
                                  （= 有 quote.create/quote.edit 權限且非已核准鎖定）；日後若已核准仍開放編輯，
                                  canInlineEdit 一旦為 true 此鈕就會自動跟著出現，不需再改這裡。
                                  外層 span 的 stopPropagation 是必要的：wrapper 的 onClick 是檔案上傳
                                  triggerImageUpload(idx)，不擋住會同時彈出檔案選擇視窗。
                                  不支援 getDisplayMedia 的瀏覽器 ScreenshotCapture 回傳 null，不渲染任何按鈕。 */}
                              {canInlineEdit && (
                                <span
                                  className="product-thumb-screenshot-btn"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ScreenshotCapture
                                    compact
                                    onUploaded={(url) => handleScreenshotUploaded(
                                      typeof item.product === 'object' ? item.product?._id : item.product,
                                      url
                                    )}
                                  />
                                </span>
                              )}
                              {/* 需求 R152（PM 2026-09-01 留言）：報價單頁的產品縮圖
                                  hover 顯示下載小圖示（右下角，與左下角截圖鈕左右對稱）。
                                  ⚠️ 與截圖鈕最大的不同：**不綁 canInlineEdit**。PM 指名的「報價單-詳情頁
                                  （已審核通過、不得更改）」就是本頁 isLocked 的狀態 —— 那時 canInlineEdit
                                  為 false，截圖鈕與「更換圖片」遮罩都不渲染；下載鈕若也綁上去，
                                  PM 要的畫面永遠出不來。下載是唯讀行為，不會改到已審核的報價單一個字。
                                  （報價單列表點任一狀態的單子都導向 /quotes/:id/edit，見 QuotesPage.js:511
                                  的 需求 R33 註解，所以「已審核通過不得更改的頁面」＝本頁鎖定狀態。）
                                  元件自帶 stopPropagation／preventDefault，可編輯狀態下點它也不會
                                  觸發外層 triggerImageUpload 的檔案選擇視窗。 */}
                              <ProductImageDownloadButton src={item.productImage} productName={item.productName} />
                            </div>
                            <div className="product-info-text">
                              {/* 需求 R25：產品資訊改顯示「產品貨編」(productCode)；早期報價單可能未把 productCode 寫入快照，
                                          這裡 fallback 到實時抓取的 _productData，避免欄位空白。 */}
                              <span className="product-code">{item.productCode || item._productData?.productCode || ''}</span>
                              <span className="product-name-zh">{item.productName}</span>
                              {canInlineEdit && editingField?.idx === idx && editingField?.field === 'englishName' ? (
                                <input
                                  autoFocus
                                  type="text"
                                  className="form-control form-control-sm qc-inline-edit-input"
                                  defaultValue={item.englishName}
                                  onBlur={(e) => {
                                    const v = e.target.value;
                                    const productId = typeof item.product === 'object' ? item.product?._id : item.product;
                                    syncProductFieldAcrossItems(productId, 'englishName', v);
                                    setEditingField(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.target.blur(); }
                                    if (e.key === 'Escape') { setEditingField(null); }
                                  }}
                                />
                              ) : (
                                <span
                                  className={`product-name-en${canInlineEdit ? ' qc-inline-editable' : ''}`}
                                  onClick={canInlineEdit ? () => setEditingField({ idx, field: 'englishName' }) : undefined}
                                  title={canInlineEdit ? '點擊編輯英文品名' : ''}
                                >
                                  {item.englishName || (canInlineEdit ? '（點擊新增英文品名）' : '')}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.5' }}>
                            <span className="qc-spec">{item.spec}</span>
                            {item.pcsPerBox && <span className="qc-pcs">{item.pcsPerBox}入</span>}
                            {/* 需求 R25：國際條碼移至規格/箱入下方；老報價單若 snapshot 無 barcode，從實時產品補齊。 */}
                            {(item.barcode || item._productData?.barcode) && (
                              <span className="qc-barcode">{item.barcode || item._productData?.barcode}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <select
                              className="form-control form-control-sm qc-tier-select"
                              value={item.priceTier}
                              onChange={e => updateItem(idx, 'priceTier', parseInt(e.target.value))}
                              disabled={isLocked}
                            >
                              <option value={1}>1階</option>
                              <option value={2}>2階</option>
                              <option value={3}>3階</option>
                              <option value={4}>4階</option>
                            </select>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.moq}
                              onChange={e => updateItem(idx, 'moq', e.target.value)}
                              disabled={isLocked}
                            />
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <input
                              type="text"
                              inputMode="decimal"
                              className="form-control form-control-sm"
                              // 需求 R84（PM 2026-07-24 拍板 D1=A/D2/D3）：單品加價需能輸入
                              // 小數，最多至小數點後第 3 位（硬限 D1=A）。改用 _editingItemMarkup 原始字串
                              // buffer 渲染，保留使用者正在輸入的中間態（'0.'、'12.3'），避免受控數字綁定
                              // 在打「.」時被 parseFloat→NaN 或 0→'' 吃掉；僅在合法時同步解析成數字供運算。
                              // D2：欄位保留 3 位輸入/儲存精度，最終單價仍走 calcUnitPrice 既有進位規則不變。
                              // D3：與建立頁 QuoteCreatePage 一致套用。
                              value={item._editingItemMarkup != null ? item._editingItemMarkup : (item.itemMarkup === 0 ? '' : item.itemMarkup)}
                              onChange={e => {
                                const v = e.target.value;
                                // D1=A：最多小數點後 3 位（\d{0,3}）、允許負號與中間態；超過 3 位或非法字元不接受。
                                if (v === '' || /^-?\d*\.?\d{0,3}$/.test(v)) {
                                  const n = (v === '' || v === '-' || v === '.' || v === '-.') ? 0 : parseFloat(v);
                                  setItems(prev => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], _editingItemMarkup: v };
                                    return next;
                                  });
                                  updateItem(idx, 'itemMarkup', isNaN(n) ? 0 : n);
                                }
                              }}
                              onBlur={() => setItems(prev => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], _editingItemMarkup: null };
                                return next;
                              })}
                              disabled={isLocked}
                            />
                            <div className="qc-labeling-cell">
                              <select
                                className="form-control form-control-sm"
                                value={item.labelingMode}
                                onChange={e => updateItem(idx, 'labelingMode', e.target.value)}
                                disabled={isLocked}
                              >
                                <option value="none">不貼標</option>
                                <option value="auto">自動計算</option>
                                <option value="manual">手動輸入</option>
                              </select>
                              {item.labelingMode === 'manual' && (
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control form-control-sm qc-labeling-manual"
                                  disabled={isLocked}
                                  value={item._editingLabelingFee != null ? item._editingLabelingFee : (item.labelingFee === 0 ? '' : item.labelingFee)}
                                  onChange={e => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                      const n = v === '' ? 0 : parseFloat(v);
                                      setItems(prev => {
                                        const next = [...prev];
                                        const fee = isNaN(n) ? 0 : n;
                                        let updated = { ...next[idx], _editingLabelingFee: v, labelingFee: fee };
                                        if (!updated.isManualPrice) {
                                          const rate = getCurrentRate();
                                          updated.unitPrice = calcUnitPrice(updated, rate, specialFormula, currency, globalMarkup);
                                        }
                                        next[idx] = updated;
                                        return next;
                                      });
                                    }
                                  }}
                                  onBlur={e => {
                                    const v = e.target.value;
                                    const n = v === '' ? 0 : parseFloat(v);
                                    setItems(prev => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], _editingLabelingFee: null, labelingFee: isNaN(n) ? 0 : n };
                                      return next;
                                    });
                                  }}
                                />
                              )}
                              {item.labelingMode === 'auto' && (
                                <span className="qc-labeling-fee-hint">{item.labelingFee}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="qc-price-cell">
                            <input
                              type="text"
                              className={`qc-price-input${item.isManualPrice ? ' qc-manual-price' : ''}`}
                              value={item._editingPrice != null ? item._editingPrice : formatPrice(item.unitPrice, currency)}
                              onChange={e => setItems(prev => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], _editingPrice: e.target.value };
                                return next;
                              })}
                              onBlur={e => {
                                const raw = e.target.value;
                                // 需求 R36：空字串／全空白等同點擊紅點還原 → 取消手動覆寫並回到 calcUnitPrice 結果
                                if (raw.trim() === '') {
                                  handleRestoreAutoPrice(idx);
                                  setItems(prev => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], _editingPrice: null };
                                    return next;
                                  });
                                  return;
                                }
                                const val = parseFloat(raw);
                                if (!isNaN(val) && val !== item.unitPrice) handleManualPrice(idx, val);
                                setItems(prev => {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], _editingPrice: null };
                                  return next;
                                });
                              }}
                              disabled={isLocked}
                            />
                            {(item.isManualPrice || item.isCostSourceChanged || isCustomCostActive || isCustomBoxCostActive || isPriceTierChanged) && (
                              <span
                                className="price-modified-dot"
                                title={(() => {
                                  // 需求 R103（Q5=A / AC16）：改為與建立頁同一套「多理由逐行列出」，
                                  // 原本的三元鏈只會顯示第一個成立的理由，補上階數後會遮蓋其他理由。
                                  const reasons = [];
                                  if (item.isManualPrice) reasons.push('單價已手動調整，點擊還原');
                                  // 需求 R149 Q6=A：兩種自訂成本的理由分開寫，看得出是哪一種在生效。
                                  if (isCustomCostActive) reasons.push('已使用自訂包成本換算');
                                  if (isCustomBoxCostActive) reasons.push('已使用自訂箱成本換算');
                                  if (item.isCostSourceChanged) reasons.push('成本來源已變更');
                                  if (isPriceTierChanged) reasons.push(`價格階已調整為非預設（預設為 ${rowDefaultTier} 階）`);
                                  return reasons.join('\n');
                                })()}
                                onClick={() => !isLocked && item.isManualPrice && handleRestoreAutoPrice(idx)}
                              >!</span>
                            )}
                            {/* 需求 R148：敏感價格變更提醒「$」。
                                ‧ 位置＝「!」右側；「!」未 render 時緊接單價輸入框右側（AC13）。
                                ‧ 與「!」互相獨立（AC12）：上面那組 5 個 OR 全不成立時，這顆仍可單獨出現。
                                ‧ 條件**不含 isLocked**（AC8）：檢視（唯讀）畫面照樣顯示，與「!」一致。
                                ‧ Q8=A（AC18）：item._productData 為 null（舊單沒有 product ref／產品已被硬刪除）
                                  時 hasSensitivePriceChange 回 false → 靜默不顯示，不報錯。
                                ‧ Q6=A（AC16）：純提示，不掛 onClick，不會改動任何欄位。
                                ‧ Q5=C ＋ PM「不會顯示明細，只有提醒作用」（AC15）：所有角色同一句通用提示，
                                  不列欄位／前後值／修改人／時間，也不夾帶金額。 */}
                            {hasSensitivePriceChange(item._productData, quoteUpdatedAt) && (
                              <span
                                className="price-alert-dot"
                                title={SENSITIVE_PRICE_ALERT_TITLE}
                              >$</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="form-control form-control-sm"
                            value={item.quantity === 0 ? '' : item.quantity}
                            onChange={e => {
                              const v = e.target.value;
                              if (v === '') { updateItem(idx, 'quantity', 0); return; }
                              const n = parseInt(v, 10);
                              if (!isNaN(n) && n >= 0) updateItem(idx, 'quantity', n);
                            }}
                            disabled={isLocked}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* 需求 R59 D7(A)：葷素備註改純文字自由輸入（推翻 需求 R34「僅下拉」拍板方向）。
                                儲存時的 _origVegetarianType 比對＋回寫 Product.vegetarianType 邏輯保留不變。 */}
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.vegetarianNote || ''}
                              placeholder="葷素備註"
                              title="葷素備註（資料來源：產品詳細頁；儲存時若異動會回寫 Product.vegetarianType）"
                              onChange={e => updateItem(idx, 'vegetarianNote', e.target.value)}
                              disabled={isLocked}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.salesNotes || ''}
                              placeholder="業務備註"
                              readOnly={!canInlineEdit}
                              disabled={isLocked}
                              // 需求 R118（Q6）：停回寫後文案不得再宣稱「儲存時回寫產品」。
                              title={canInlineEdit ? '業務備註（僅套用於本張報價單，不回寫產品）' : '業務備註（唯讀）'}
                              // 需求 R118（Q7=B「取消連動」）：只改當下這一列，
                              // 不再用 syncProductFieldAcrossItems 把同一產品的其他列一起改。
                              onChange={e => {
                                if (!canInlineEdit) return;
                                updateItem(idx, 'salesNotes', e.target.value);
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="row-actions">
                            <button
                              className={`btn-icon-sm btn-icon-sm-outline${infoPanel === idx ? ' qc-eye-btn-active' : ''}`}
                              title="產品資訊"
                              onClick={() => setInfoPanel(infoPanel === idx ? null : idx)}
                            >
                              👁
                            </button>
                            {!isLocked && (
                              <button
                                className="btn-icon-sm btn-icon-sm-danger"
                                onClick={() => removeItem(idx)}
                                title="移除此品項"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
            )}
          </div>
        </section>

        {/* ========== SECTION 4: 交易細節 ========== */}
        <section className="qc-section">
          <div className="qc-section-header">
            <div className="qc-section-header-left">
              <span className="qc-section-number">4</span>
              交易細節與備註
            </div>
          </div>
          <div className="qc-section-body">
            {/* 需求 R143（PM 2026-08-29 拍板 Q2=A）：Section 4 改三欄並排
                ——「交易條件 ／ 備註 ／（建立者＋聯絡人）」，版面與建立頁一致（AC2）。
                交易條件欄除了寬度變窄之外，必填、抓取前次交易記錄按鈕、isLocked 唯讀行為
                一律維持原狀（AC3）。 */}
            <div className="footer-grid">
              {/* Left column: 交易條件（＝原本的「交易條件備註」，Q3=A 只改標籤名稱） */}
              <div className="notes-left">
                <label>交易條件 <span className="qc-required">*</span></label>
                <textarea
                  className="form-control qc-notes-textarea"
                  value={transactionNotes}
                  onChange={e => setTransactionNotes(e.target.value)}
                  rows={4}
                  placeholder="輸入交易條件，或點擊下方按鈕抓取前次交易記錄..."
                  disabled={isLocked}
                />
                {/* 需求 R143 Q4=A：「抓取前次交易記錄」只服務交易條件，備註欄不得有這顆按鈕。 */}
                <button
                  className="qc-btn qc-btn-outline qc-btn-sm notes-bottom-btn"
                  onClick={fetchLastTransaction}
                  disabled={!selectedCustomer || isLocked}
                  title={!selectedCustomer ? '請先選擇客戶' : ''}
                >
                  抓取前次交易記錄
                </button>
              </div>
              {/* Middle column: 備註（需求 R143 新增）
                  ⚠️ 本欄內容**不會**出現在匯出的 PDF／Excel（AC6-AC7）：buildExportQuoteObj
                     刻意不帶 internalNotes，兩支 exporter 也只讀 transactionNotes。
                  ⚠️ 需求 R162（PM 2026-09-06 Q1=A／Q3=A／Q5=A）：本欄**已脫離** isLocked，
                     改綁 canEditInternalNotes（＝quote.edit 權限），四種狀態一律可編輯。
                     左欄「交易條件」仍吃 isLocked ── 兩欄行為刻意不同，這是 PM 拍板的結果，
                     不是漏改；日後要對齊請先確認決議是否被推翻。 */}
              <div className="notes-middle">
                <label>備註</label>
                <textarea
                  className="form-control qc-notes-textarea"
                  value={internalNotes}
                  onChange={e => setInternalNotes(e.target.value)}
                  rows={4}
                  placeholder="內部備註（不會顯示在匯出的報價單上）..."
                  disabled={!canEditInternalNotes}
                />
                {/* 需求 R162 Q1=A：唯讀（已審核／待審核非主管）時底部動作列整區不渲染，
                    這顆緊貼欄位的小鈕就是備註唯一的儲存動線；只送 internalNotes，
                    存完停在原畫面不跳頁（AC14）。非唯讀狀態不顯示——底部「儲存」大鈕已涵蓋。 */}
                {showNotesOnlySave && (
                  <button
                    className="qc-btn qc-btn-outline qc-btn-sm notes-bottom-btn"
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    title="只儲存備註，不影響此報價單的其他欄位"
                  >
                    {savingNotes ? '儲存中…' : '儲存備註'}
                  </button>
                )}
              </div>
              {/* Right column: 建立者 ＋ 聯絡人 */}
              <div>
                {/* 需求 R143（AC10／Q6=A）：建立者排在「聯絡人 (業務)」之上，顯示該單
                    **原始建單人**（不是目前登入者）。readOnly＋tabIndex=-1 ＝不可輸入／不可清空；
                    不進 payload、不參與驗證（AC12），後端 update 也已剔除 createdBy（AC14）。 */}
                <div className="form-group">
                  <label>建立者</label>
                  <input
                    type="text"
                    className="form-control qc-readonly-field"
                    value={createdByName || '—'}
                    readOnly
                    tabIndex={-1}
                  />
                </div>
                <div className="form-group">
                  <label>聯絡人 (業務) <span className="qc-required">*</span></label>
                  <select
                    className="form-control"
                    value={contactPerson}
                    onChange={e => setContactPerson(e.target.value)}
                    disabled={isLocked}
                  >
                    <option value="">— 請選擇 —</option>
                    {salesUsers.map(u => (
                      <option key={u._id} value={u._id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                {(() => {
                  const selectedSalesUser = salesUsers.find(u => u._id === contactPerson);
                  if (!selectedSalesUser) return null;
                  return (
                    <div className="contact-info-card">
                      <div className="contact-info-row">
                        <span className="contact-info-label">姓名</span>
                        <span className="contact-info-value">{selectedSalesUser.name}</span>
                      </div>
                      {/* 需求 R81（PM 2026-07-22）：聯絡人聯絡欄位改 E-mail，畫面預覽不再顯示電話 */}
                      {selectedSalesUser.email && (
                        <div className="contact-info-row">
                          <span className="contact-info-label">E-mail</span>
                          <span className="contact-info-value">{selectedSalesUser.email}</span>
                        </div>
                      )}
                      {selectedSalesUser.lineId && (
                        <div className="contact-info-row">
                          <span className="contact-info-label">通訊方式</span>
                          <span className="contact-info-value">{selectedSalesUser.lineId}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* Bottom action bar */}
        <div className="qc-bottom-bar">
          <div className="action-bar-left">
            最後編輯: {lastEditTime || '尚未儲存'}
          </div>
          <div className="action-bar-right">
            <button className="qc-btn qc-btn-outline" onClick={() => setShowCancelModal(true)}>
              {isLocked ? '返回' : '取消'}
            </button>
            {/* 需求 R119（PM 2026-08-18 拍板 Q2=A／Q3=A／Q4=A）：唯讀（isLocked）時為純檢視模式，
                隱藏所有寫入動作按鈕，避免使用者誤觸後被後端拒絕；畫面只留「返回」與（可列印時的）「列印」。
                isLocked 現在同時涵蓋「已審核」與「待審核且非主管」兩種情形。
                ⚠️ 舊註解原寫「pending + sales 仍可看到『儲存 / 儲存並審核』（PM 2026-05-22 修正條件已開放）」，
                   該 需求 R33 決議已被本票 Q3=A 明確反轉，故一併更新，避免留下互相矛盾的說明。 */}
            {!isLocked && (
              <>
                <button className="qc-btn qc-btn-secondary" onClick={() => handleSave(false)} disabled={saving}>
                  {saving ? '儲存中…' : (quoteStatus === 'pending' ? '儲存' : '暫存草稿')}
                </button>
                <button className={`qc-btn ${quoteStatus === 'pending' ? 'qc-btn-success' : 'qc-btn-primary'}`} onClick={() => handleSave(true)} disabled={saving}>
                  {saving ? '送出中…' : (quoteStatus === 'pending' ? '儲存並審核' : '送出審核')}
                </button>
              </>
            )}
            {/* 需求 R125（PM 2026-08-21 拍板）：待審核單的主管在編輯頁同時看得到
                「儲存並審核」（→ approved）與「審核退回」（→ rejected）兩條出路；
                在此之前編輯頁只有核准一條路，退回鈕只存在於詳情頁，主管走列表→編輯頁的動線就找不到。
                canReject = pending && manager，與詳情頁 QuoteDetailPage.js:227/247 同一套條件；
                非主管或非待審核狀態一律不渲染（且後端另有 403/400 閘門）。 */}
            {canReject && (
              <button
                className="qc-btn qc-btn-danger"
                onClick={() => setShowRejectModal(true)}
                disabled={saving || rejecting}
              >
                {rejecting ? '退回中…' : '審核退回'}
              </button>
            )}
            {/* 需求 R119（Q1=B）：草稿 / 退回單不給列印——連按鈕都不渲染，
                匯出 handler 沒有任何殘留入口可觸發。待審核 / 已審核維持可列印。 */}
            {canPrint && (
              <div className="qc-export-wrapper" ref={exportRef}>
                <button className="qc-btn qc-btn-outline" onClick={() => setShowExportMenu(!showExportMenu)}>
                  列印
                </button>
                {showExportMenu && (
                  <div className="qc-export-menu">
                    {/* 需求 R168 Q4=A：三顆、順序 PDF → Excel 標準 → Excel 預購表，與詳情頁 .qd-export-menu 同步 */}
                    <button className="qc-export-option" onClick={handleExportPDF}>PDF 標準報價單</button>
                    <button className="qc-export-option" onClick={handleExportExcel}>Excel 標準報價單</button>
                    <button className="qc-export-option" onClick={handleExportPreorderExcel}>Excel 預購表用</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Info Drawer */}
      {(() => {
        const drawerItem = infoPanel != null ? items[infoPanel] : null;
        const drawerPd = drawerItem?._productData;
        const drawerCostEntry = drawerItem?.costSource === 'factory'
          ? drawerPd?.factoryCost
          : drawerPd?.distributorCosts?.[parseInt((drawerItem?.costSource || '').split('_')[1], 10)];
        const volumeMethodLabel = (m) => m === 'auto' ? '自動計算' : m === 'supplier' ? '廠商提供' : m === 'estimate' ? '預估' : '—';
        return (
          <>
            <div
              className={`qc-product-drawer-overlay${infoPanel != null ? ' active' : ''}`}
              onClick={() => setInfoPanel(null)}
            />
            <div className={`qc-product-drawer${infoPanel != null ? ' active' : ''}`}>
              {drawerItem && (
                <>
                  <div className="qc-product-drawer-header">
                    <h3>產品資訊 - {drawerItem.productName}</h3>
                    <button className="qc-product-drawer-close" onClick={() => setInfoPanel(null)}>&times;</button>
                  </div>
                  <div className="qc-product-drawer-body">
                    {!drawerPd ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#8D6E63' }}>產品資料載入中...</div>
                    ) : (
                      <>
                        <div className="qc-info-section">
                          <div className="qc-info-section-title">標準成本資料 (報價來源)</div>
                          <table className="qc-info-cost-table">
                            <thead>
                              <tr><th>選取</th><th>成本來源</th><th>標準成本</th><th>MOQ</th></tr>
                            </thead>
                            {['factory', ...(drawerPd?.distributorCosts || []).map((_, i) => `distributor_${i}`)].map(src => {
                              const entry = src === 'factory' ? drawerPd?.factoryCost : drawerPd?.distributorCosts?.[parseInt(src.split('_')[1], 10)];
                              const label = src === 'factory' ? '工廠' : `經銷${parseInt(src.split('_')[1]) + 1}`;
                              const isActive = drawerItem.costSource === src;
                              const activeCls = isActive ? 'qc-info-cost-active' : '';
                              return (
                                <tbody key={src} className={`qc-info-cost-group ${isActive ? 'qc-info-cost-group-active' : ''}`}>
                                  <tr
                                    className={`qc-info-cost-main-row${isLocked ? '' : ' qc-info-cost-row-clickable'} ${activeCls}`}
                                    onClick={isLocked ? undefined : () => handleCostSourceChange(infoPanel, src)}
                                  >
                                    <td><input type="radio" name="cost-source-drawer" checked={drawerItem.costSource === src} onChange={() => handleCostSourceChange(infoPanel, src)} onClick={(e) => e.stopPropagation()} disabled={isLocked} /></td>
                                    <td>{label}</td>
                                    {/* 需求 R132：與建立頁抽屜同步，顯示「實際代入單價算式」的未進位成本
                                        （箱價÷箱入數；PM 案例 59.85），不再顯示 DB 裡已進位的 unitPrice 59.9。
                                        產品管理頁依 需求 R70 仍顯示 59.9。 */}
                                    <td className="qc-info-cost-price">${formatBaseCost(resolveEntryBaseCost(entry, drawerPd?.pcsPerBox)) ?? '—'}</td>
                                    <td>{entry?.moq || '—'}</td>
                                  </tr>
                                  <tr
                                    className={`qc-info-cost-notes-row${isLocked ? '' : ' qc-info-cost-row-clickable'} ${activeCls}`}
                                    onClick={isLocked ? undefined : () => handleCostSourceChange(infoPanel, src)}
                                  >
                                    <td colSpan={4} className="qc-info-cost-notes-cell">
                                      <span className="qc-info-cost-notes-label">備註：</span>
                                      <span className="qc-info-cost-notes-text">{entry?.notes || '（無）'}</span>
                                    </td>
                                  </tr>
                                </tbody>
                              );
                            })}
                          </table>
                        </div>

                        {/* 需求 R32 / 需求 R149: 自訂成本換算（僅影響本筆報價單 item，不回寫商品主檔） */}
                        {(() => {
                          const drawerSourceCost = getBaseCostFromSource(drawerPd, drawerItem.costSource);
                          const customValNum = Number(drawerItem.customCostValue);
                          const customValValid = Number.isFinite(customValNum) && customValNum > 0;
                          const isCustomCostActiveDrawer =
                            drawerItem.customCostEnabled
                            && customValValid
                            && customValNum !== Number(drawerSourceCost);
                          const hint = !drawerItem.customCostEnabled
                            ? '啟用後以自填包成本取代來源成本進行單價計算'
                            : (customValValid
                                ? (isCustomCostActiveDrawer
                                    ? `目前以自訂包成本 ${customValNum} 取代來源成本 ${drawerSourceCost ?? '—'} 進行計算`
                                    : '目前自訂包成本與來源成本相同，計算結果不變')
                                : '目前仍使用原成本來源計算');
                          const editing = drawerItem._editingCustomCost;
                          const inputValue = editing != null
                            ? editing
                            : (customValValid ? customValNum : '');

                          // ── 需求 R149：右欄「自訂箱成本」（與建立頁同一套判斷，換算一律走 util）──
                          const boxCostAllowed = canUseCustomBoxCost(drawerItem);
                          const boxValNum = Number(drawerItem.customBoxCostValue);
                          const boxValValid = Number.isFinite(boxValNum) && boxValNum > 0;
                          const boxPackCost = packCostFromCustomBoxCost(drawerItem.customBoxCostValue, drawerItem.pcsPerBox);
                          const isCustomBoxCostActiveDrawer =
                            drawerItem.customBoxCostEnabled
                            && boxPackCost != null
                            && boxPackCost !== Number(drawerSourceCost);
                          const boxHint = !boxCostAllowed
                            ? CUSTOM_BOX_COST_NO_PCS_HINT
                            : (!drawerItem.customBoxCostEnabled
                                ? '啟用後以自填箱成本 ÷ 箱入數換算出包成本，再取代來源成本計算'
                                : (boxValValid
                                    ? (isCustomBoxCostActiveDrawer
                                        ? `目前以自訂箱成本 ${boxValNum} ÷ 箱入數 ${drawerItem.pcsPerBox} ＝ 包成本 ${boxPackCost} 取代來源成本 ${drawerSourceCost ?? '—'} 進行計算`
                                        : '目前換算出的包成本與來源成本相同，計算結果不變')
                                    : '目前仍使用原成本來源計算'));
                          const editingBox = drawerItem._editingCustomBoxCost;
                          const boxInputValue = editingBox != null
                            ? editingBox
                            : (boxValValid ? boxValNum : '');
                          return (
                            <div className="qc-info-section qc-info-custom-cost-section">
                              {/* 需求 R149 Q5=A：畫面用字統一為「訂」。 */}
                              <div className="qc-info-section-title">自訂成本換算</div>
                              {/* 需求 R149 需求三：一列二欄、左右排列（Q10=A 窄螢幕塌成單欄，見 CSS）。 */}
                              <div className="qc-info-detail-block qc-info-custom-cost-grid">
                                <div className={`qc-info-custom-cost-col${isCustomCostActiveDrawer ? ' qc-info-custom-cost-col-active' : ''}`}>
                                  <label className="qc-info-custom-cost-toggle">
                                    <input
                                      type="checkbox"
                                      checked={!!drawerItem.customCostEnabled}
                                      onChange={(e) => handleCustomCostToggle(infoPanel, e.target.checked)}
                                      /* 需求 R133 Q1=B：主管在待審核單可直接修改（見 canEditCustomCost 註解）。 */
                                      disabled={!canEditCustomCost}
                                    />
                                    <span>使用自訂包成本換算</span>
                                  </label>
                                  <div className="qc-info-custom-cost-input-row">
                                    <input
                                      type="number"
                                      inputMode="decimal"
                                      step="any"
                                      min="0"
                                      className="form-control form-control-sm qc-info-custom-cost-input"
                                      /* 需求 R133 Q1=B：同上，鎖定條件改綁 canEditCustomCost。 */
                                      disabled={!canEditCustomCost || !drawerItem.customCostEnabled}
                                      value={inputValue}
                                      onChange={(e) => handleCustomCostChange(infoPanel, e.target.value)}
                                      onBlur={() => setItems(prev => {
                                        const next = [...prev];
                                        next[infoPanel] = { ...next[infoPanel], _editingCustomCost: undefined };
                                        return next;
                                      })}
                                    />
                                    {/*
                                      需求 R105（PM 2026-08-04 拍板 Q1=A / Q2=A / Q3=A / Q4=A）：
                                      此處原本有一顆幣別標籤 <span>，內容是 drawerPd?.currency || currency
                                      || 'TWD'，已刪除；Q2=A 連同它專用的 CSS 規則一起移除（樣式與本頁
                                      共用 QuoteCreatePage.css）。
                                      Q4=A 要求建立頁與編輯頁兩處同時修改；本頁與 QuoteCreatePage.js 是
                                      兩份獨立程式碼，改一邊會出現「建立頁沒幣別、編輯頁還有」的不一致。
                                      刪除理由同建立頁：此欄填的是取代來源標準成本的成本基準，與報價單
                                      輸出幣別無關，標幣別會誤導。Q3=A：版面與輸入框寬度不動。
                                      需求 R149 沿用同一條決議：新增的箱成本輸入框旁同樣不標幣別。
                                    */}
                                  </div>
                                  <div className="qc-info-custom-cost-hint">{hint}</div>
                                </div>
                                <div className={`qc-info-custom-cost-col${isCustomBoxCostActiveDrawer ? ' qc-info-custom-cost-col-active' : ''}${boxCostAllowed ? '' : ' qc-info-custom-cost-col-disabled'}`}>
                                  <label className="qc-info-custom-cost-toggle">
                                    <input
                                      type="checkbox"
                                      checked={!!drawerItem.customBoxCostEnabled}
                                      /* Q8=A 權限與包成本完全相同；Q4=A 箱入數缺漏時整欄停用。 */
                                      disabled={!canEditCustomCost || !boxCostAllowed}
                                      onChange={(e) => handleCustomBoxCostToggle(infoPanel, e.target.checked)}
                                    />
                                    <span>使用自訂箱成本換算</span>
                                  </label>
                                  <div className="qc-info-custom-box-cost-input-row">
                                    <input
                                      type="number"
                                      inputMode="decimal"
                                      step="any"
                                      min="0"
                                      className="form-control form-control-sm qc-info-custom-cost-input"
                                      disabled={!canEditCustomCost || !boxCostAllowed || !drawerItem.customBoxCostEnabled}
                                      value={boxInputValue}
                                      onChange={(e) => handleCustomBoxCostChange(infoPanel, e.target.value)}
                                      onBlur={() => setItems(prev => {
                                        const next = [...prev];
                                        next[infoPanel] = { ...next[infoPanel], _editingCustomBoxCost: undefined };
                                        return next;
                                      })}
                                    />
                                  </div>
                                  <div className="qc-info-custom-cost-hint">{boxHint}</div>
                                </div>
                              </div>
                              {/*
                                需求 R133 Q3=A（PM 2026-08-21）：修復前存檔的舊單，
                                自訂成本的旗標與數值已被 mongoose strict mode 丟棄，但數字仍殘留在
                                item.baseCost。這裡把「為什麼這列的單價被凍住」講清楚，並給出唯一
                                兩條出路：重新輸入自訂成本（上方輸入框），或明確改用來源成本重算。
                                需求 R149：二欄化之後，這段警示改掛在 grid **外面**（整個區塊層級）——
                                它講的是「這一列」的狀態，不專屬於左右任一欄，塞進其中一欄會被壓成半寬。
                              */}
                              {drawerItem._legacyCustomCostGuard && (
                                <div className="qc-info-custom-cost-hint qc-legacy-custom-cost-warning">
                                  ⚠ 此列存檔的成本基準（{drawerItem.baseCost}）與目前的來源成本
                                  （{drawerSourceCost ?? '—'}）不符，可能是本次修復前遺失的自訂成本。
                                  為避免單價無聲變動，已暫停本列的自動重算，也暫時不能切換成本來源。
                                  <button
                                    type="button"
                                    className="qc-btn qc-btn-outline qc-btn-sm"
                                    disabled={!canEditCustomCost}
                                    onClick={() => clearLegacyCustomCostGuard(infoPanel)}
                                  >改用來源成本重算</button>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        <hr className="qc-info-divider" />
                        <div className="qc-info-section">
                          <div className="qc-info-section-title">
                            材積來源
                            <span className={`qc-info-volume-light ${drawerPd?.volumeMethod === 'estimate' ? 'qc-info-light-red' : 'qc-info-light-yellow'}`} />
                          </div>
                          <div className="qc-info-detail-block">
                            <div className="qc-info-detail-row">
                              <span className="qc-info-detail-label">資料來源：</span>
                              <span className="qc-info-detail-value">{volumeMethodLabel(drawerPd?.volumeMethod)}</span>
                            </div>
                            <div className="qc-info-detail-row">
                              <span className="qc-info-detail-label">才數 / CBM：</span>
                              <span className="qc-info-detail-value">
                                {drawerPd?.volumeCuft != null ? `${parseFloat(drawerPd.volumeCuft).toFixed(2)} 才` : '—'} / {drawerPd?.volumeCbm != null ? `${parseFloat(drawerPd.volumeCbm).toFixed(3)} CBM` : '—'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <hr className="qc-info-divider" />
                        <div className="qc-info-section">
                          <div className="qc-info-section-title">產品備註</div>
                          <div className="qc-info-notes-block">
                            {(drawerCostEntry?.notes || drawerPd?.productNotes) || '（無備註）'}
                          </div>
                        </div>
                        <hr className="qc-info-divider" />
                        <div className="qc-info-section">
                          <div className="qc-info-section-title">出貨地區</div>
                          <div className="qc-info-shipping-block">
                            {drawerCostEntry?.shippableCountries && (
                              <div className="qc-info-shipping-row">
                                <span className="qc-info-shipping-ok">✓ 可出貨國家</span>
                                <div className="qc-info-shipping-countries">{drawerCostEntry.shippableCountries}</div>
                              </div>
                            )}
                            {drawerCostEntry?.nonShippableCountries && (
                              <div className="qc-info-shipping-row">
                                <span className="qc-info-shipping-no">✕ 不可出貨國家</span>
                                <div className="qc-info-shipping-countries">{drawerCostEntry.nonShippableCountries}</div>
                              </div>
                            )}
                            {!drawerCostEntry?.shippableCountries && !drawerCostEntry?.nonShippableCountries && (
                              <div className="qc-info-shipping-empty">（未設定出貨地區資料）</div>
                            )}
                          </div>
                        </div>
                        <hr className="qc-info-divider" />
                        <div className="qc-info-section qc-info-section-center">
                          <button className="qc-btn qc-btn-detail" onClick={() => window.open(`/products/${typeof drawerItem.product === 'object' ? drawerItem.product?._id : drawerItem.product}`, '_blank')}>
                            查看產品詳細資料
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* Hidden file input for product image upload */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleImageFileChange}
      />

      {/* Toast notifications */}
      {toasts.length > 0 && (
        <div className="qc-toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`qc-toast qc-toast-${t.type}`}>
              {t.message}
            </div>
          ))}
        </div>
      )}

      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <div className="qc-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="qc-modal" onClick={e => e.stopPropagation()}>
            <div className="qc-modal-title">確認取消</div>
            <div className="qc-modal-body">確定要離開編輯頁面嗎？未儲存的內容將會遺失。</div>
            <div className="qc-modal-actions">
              <button className="qc-btn qc-btn-secondary" onClick={() => setShowCancelModal(false)}>
                繼續編輯
              </button>
              {/* 需求 R75：取消／返回離開編輯頁面時，回到「報價單列表」（/quotes），
                  而非停留在該報價單的檢視畫面（/quotes/:id）。儲存成功後（L887/L889）仍導向檢視畫面不變。 */}
              <button className="qc-btn qc-btn-danger" onClick={() => { setShowCancelModal(false); navigate('/quotes'); }}>
                確定取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 需求 R125：審核退回確認視窗——退回是不可逆的狀態變更，且**不會**儲存畫面上的編輯內容，
          兩件事都在 body 明講，避免主管誤按或誤以為改動一併保存。 */}
      {showRejectModal && (
        <div className="qc-modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="qc-modal" onClick={e => e.stopPropagation()}>
            <div className="qc-modal-title">確認審核退回</div>
            <div className="qc-modal-body">
              確定要將這張報價單退回修改嗎？退回後狀態會變為「退回修改」，由業務重新調整後再送審。
              <br />
              注意：目前畫面上尚未儲存的修改不會被保存。
            </div>
            <div className="qc-modal-actions">
              <button className="qc-btn qc-btn-secondary" onClick={() => setShowRejectModal(false)}>
                繼續編輯
              </button>
              <button className="qc-btn qc-btn-danger" onClick={handleReject} disabled={rejecting}>
                {rejecting ? '退回中…' : '確定退回'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteEditPage;
